import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Slider } from './ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import AnalyticsTab from './AnalyticsTab';
import ComposeTab from './ComposeTab';
import SiteSettingsTab from './SiteSettingsTab';
import {
  LogOut, CheckCircle, XCircle, Loader2, ExternalLink, Copy,
  Send, Twitter, MessageSquare, Bookmark, SkipForward, Star,
  Activity, Settings, Users, Zap, Brain, RefreshCw, ChevronRight,
  ChevronLeft, BarChart3, Play, Search, Plus, Trash2, Eye,
  Save, X, AlertCircle, Heart, Repeat, Clock, TrendingUp,
  Calendar, Hash, Sparkles, Power, Pause
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============ MAIN DASHBOARD ============
const SentinelDashboard = () => {
  const { user, logout, loading: authLoading, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Data state
  const [agentStatus, setAgentStatus] = useState({ status: 'idle', next_run_minutes: 15, agent_enabled: true });
  const [pendingTweets, setPendingTweets] = useState([]);
  const [pendingAccounts, setPendingAccounts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [savedTweets, setSavedTweets] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistCount, setWatchlistCount] = useState(0);
  const [responses, setResponses] = useState([]);
  const [scanStats, setScanStats] = useState(null);
  const [settings, setSettings] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [copiedText, setCopiedText] = useState(null);
  const [activityPanelCollapsed, setActivityPanelCollapsed] = useState(false);
  const [twitterAuth, setTwitterAuth] = useState(null);
  const [sseConnected, setSseConnected] = useState(false);
  const [activeTab, setActiveTab] = useState('tweets');
  const [watchlistFilter, setWatchlistFilter] = useState('all');
  const [newHandle, setNewHandle] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showTwitterModal, setShowTwitterModal] = useState(false);
  const [twitterModalDismissed, setTwitterModalDismissed] = useState(() => {
    // Check if user dismissed the modal recently (within 24 hours)
    const dismissed = localStorage.getItem('twitterModalDismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const hoursSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60);
      return hoursSinceDismissed < 24;
    }
    return false;
  });
  
  // Result modal state (for post success/error)
  const [resultModal, setResultModal] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
    tweetUrl: null
  });

  const showResultModal = (type, title, message, tweetUrl = null) => {
    setResultModal({ isOpen: true, type, title, message, tweetUrl });
  };

  const closeResultModal = () => {
    setResultModal(prev => ({ ...prev, isOpen: false }));
  };
  
  // SSE connection ref
  const eventSourceRef = useRef(null);

  // Fetch all data
  const fetchData = useCallback(async () => {
    const authHeaders = getAuthHeaders();
    try {
      const [statusRes, queueRes, candidatesRes, savedRes, twitterRes, statsRes, watchlistRes, responsesRes, settingsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/agent/status`, { credentials: 'include', headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/twitter/queue`, { credentials: 'include', headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/sentinel/v2/candidates?status=pending`, { credentials: 'include', headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/twitter/saved`, { credentials: 'include', headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/twitter/auth/status`, { credentials: 'include', headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/sentinel/last-scan-stats`, { credentials: 'include', headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/sentinel/v2/watchlist`, { credentials: 'include', headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/twitter/responses`, { credentials: 'include', headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/sentinel/v2/settings`, { credentials: 'include', headers: authHeaders })
      ]);
      
      if (statusRes.ok) {
        const status = await statusRes.json();
        setAgentStatus(status);
        setWatchlistCount(status.watchlist_count || 0);
      }
      
      if (queueRes.ok) {
        const queue = await queueRes.json();
        // Sort by queued_at ascending so oldest (top of list) stays stable, new items at bottom
        const pending = queue.filter(t => t.status === 'pending');
        pending.sort((a, b) => new Date(a.queued_at || 0) - new Date(b.queued_at || 0));
        setPendingTweets(pending);
      }
      
      if (candidatesRes.ok) {
        const candidates = await candidatesRes.json();
        setPendingAccounts(candidates);
      }
      
      if (savedRes.ok) {
        const saved = await savedRes.json();
        setSavedTweets(saved);
      }
      
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setScanStats(stats);
      }
      
      if (twitterRes.ok) {
        const twitter = await twitterRes.json();
        setTwitterAuth(twitter);
      }
      
      if (watchlistRes.ok) {
        const wl = await watchlistRes.json();
        setWatchlist(wl);
        setWatchlistCount(wl.length);
      }
      
      if (responsesRes.ok) {
        const resp = await responsesRes.json();
        setResponses(resp);
      }
      
      if (settingsRes.ok) {
        const s = await settingsRes.json();
        setSettings(s);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Fetch recent activities
  const fetchActivities = useCallback(async () => {
    const authHeaders = getAuthHeaders();
    try {
      const res = await fetch(`${BACKEND_URL}/api/activity/recent?limit=100`, {
        credentials: 'include',
        headers: authHeaders
      });
      if (res.ok) {
        const data = await res.json();
        setActivities(data);
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    }
  }, [getAuthHeaders]);

  // Connect to SSE stream
  const connectSSE = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    
    // Get session token from localStorage for SSE authentication
    // EventSource doesn't support Authorization headers, so we pass token as query param
    const sessionToken = localStorage.getItem('wyco_session_token');
    
    if (!sessionToken) {
      // No token available, rely on polling instead
      console.log('SSE: No session token, using polling fallback');
      return;
    }
    
    const eventSource = new EventSource(
      `${BACKEND_URL}/api/activity/stream?token=${encodeURIComponent(sessionToken)}`
    );
    
    eventSource.onopen = () => {
      setSseConnected(true);
    };
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Deduplicate: only add if this timestamp doesn't already exist
        setActivities(prev => {
          const exists = prev.some(a => a.timestamp === data.timestamp);
          if (exists) return prev; // Skip duplicate
          return [data, ...prev].slice(0, 200);
        });
        
        // Auto-refresh data on certain events
        if (['cycle_complete', 'queue', 'evaluate_result'].includes(data.event_type)) {
          fetchData();
        }
      } catch (e) {
        console.error('Failed to parse SSE:', e);
      }
    };
    
    eventSource.onerror = () => {
      setSseConnected(false);
      eventSource.close();
      // Fall back to polling silently
    };
    
    eventSourceRef.current = eventSource;
  };

  // Poll for activities as fallback - but use deduplication
  const pollActivities = useCallback(async () => {
    if (sseConnected) return; // Don't poll if SSE is working
    const authHeaders = getAuthHeaders();
    try {
      const res = await fetch(`${BACKEND_URL}/api/activity/recent?limit=100`, {
        credentials: 'include',
        headers: authHeaders
      });
      if (res.ok) {
        const data = await res.json();
        // Use functional update to deduplicate based on timestamp
        setActivities(prev => {
          // Create a map of existing activities by timestamp to dedupe
          const existingTimestamps = new Set(prev.map(a => a.timestamp));
          const newActivities = data.filter(a => !existingTimestamps.has(a.timestamp));
          
          // If we have new activities, merge them; otherwise replace to avoid stale data
          if (newActivities.length > 0) {
            // Merge and sort by timestamp (newest first), keep max 200
            const merged = [...newActivities, ...prev];
            merged.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            return merged.slice(0, 200);
          } else if (prev.length === 0) {
            // Initial load
            return data;
          }
          return prev; // No change needed
        });
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    }
  }, [sseConnected, getAuthHeaders]);

  // Send heartbeat to keep agent running (prevent auto-pause)
  const sendHeartbeat = useCallback(async () => {
    const authHeaders = getAuthHeaders();
    try {
      const res = await fetch(`${BACKEND_URL}/api/activity/heartbeat`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders
      });
      if (res.ok) {
        const data = await res.json();
        // Update agent status if it changed (e.g., resumed from pause)
        if (data.agent_status && data.agent_status !== agentStatus.status) {
          fetchData();
        }
      }
    } catch (error) {
      console.error('Failed to send heartbeat:', error);
    }
  }, [getAuthHeaders, agentStatus.status, fetchData]);

  useEffect(() => {
    // Don't redirect while still checking auth
    if (authLoading) return;
    
    if (!user) {
      navigate('/');  // Go to homepage instead of login
      return;
    }
    
    fetchData();
    fetchActivities();
    connectSSE();
    sendHeartbeat(); // Send initial heartbeat on load
    
    // Refresh data every 30 seconds
    const dataInterval = setInterval(fetchData, 30000);
    
    // Poll activities every 10 seconds as fallback (only if SSE isn't working)
    const activityInterval = setInterval(pollActivities, 10000);
    
    // Send heartbeat every 3 minutes to keep agent running
    const heartbeatInterval = setInterval(sendHeartbeat, 180000);
    
    return () => {
      clearInterval(dataInterval);
      clearInterval(activityInterval);
      clearInterval(heartbeatInterval);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [user, authLoading, navigate, fetchData, pollActivities, sendHeartbeat]);

  // Show Twitter connection modal when not connected (after initial load)
  useEffect(() => {
    if (!loading && twitterAuth !== null) {
      if (twitterAuth?.connected) {
        // Auto-close modal when connected
        setShowTwitterModal(false);
      } else if (!twitterModalDismissed) {
        // Show modal after a short delay if not connected and not dismissed
        const timer = setTimeout(() => setShowTwitterModal(true), 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [loading, twitterAuth, twitterModalDismissed]);

  const dismissTwitterModal = () => {
    setShowTwitterModal(false);
    setTwitterModalDismissed(true);
    localStorage.setItem('twitterModalDismissed', Date.now().toString());
  };

  // Handle Twitter OAuth callback
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    
    if (code && state) {
      // Prevent duplicate processing
      const callbackKey = `twitter_callback_${state}`;
      if (sessionStorage.getItem(callbackKey)) {
        // Already processed this callback, just clear URL
        navigate('/sentinel', { replace: true });
        return;
      }
      sessionStorage.setItem(callbackKey, 'processing');
      
      const handleCallback = async () => {
        const authHeaders = getAuthHeaders();
        try {
          const res = await fetch(`${BACKEND_URL}/api/twitter/auth/callback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders },
            credentials: 'include',
            body: JSON.stringify({ code, state })
          });
          
          if (res.ok) {
            sessionStorage.setItem(callbackKey, 'success');
            // Clear the URL params
            navigate('/sentinel', { replace: true });
            // Refresh Twitter auth status
            fetchData();
          } else {
            const data = await res.json();
            // Only log error if it's not a duplicate/state issue
            if (!data.detail?.includes('Invalid state') && !data.detail?.includes('already connected')) {
              console.error('Twitter callback failed:', data.detail);
            }
            navigate('/sentinel', { replace: true });
          }
        } catch (error) {
          console.error('Twitter callback error:', error);
          navigate('/sentinel', { replace: true });
        }
      };
      
      handleCallback();
    }
  }, [searchParams, navigate, getAuthHeaders, fetchData]);

  // ============ ACTIONS ============
  
  const postResponse = async (queueId, draftId, text, directPost = false) => {
    const authHeaders = getAuthHeaders();
    setActionLoading(`post_${queueId}_${draftId}`);
    
    // Store the item for potential restoration on error
    const itemToRemove = pendingTweets.find(t => t.queue_id === queueId);
    
    try {
      if (!twitterAuth?.connected || !directPost) {
        await navigator.clipboard.writeText(text);
        setCopiedText(draftId);
        setTimeout(() => setCopiedText(null), 2000);
        showResultModal('copied', 'Copied to Clipboard', 'Connect your X account to post directly, or use "Copied & Posted" to confirm.');
      } else {
        // Optimistically remove from UI immediately
        setPendingTweets(prev => prev.filter(item => item.queue_id !== queueId));
        
        const res = await fetch(`${BACKEND_URL}/api/twitter/queue/${queueId}/post`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          credentials: 'include',
          body: JSON.stringify({ draft_id: draftId, custom_text: text, as_reply: true })
        });
        
        const data = await res.json();
        
        if (data.status === 'posted' && data.tweet_url) {
          showResultModal('success', 'Reply Posted!', 'Your reply has been published successfully.', data.tweet_url);
          // Success - keep item removed, fetch fresh data in background
          fetchData();
        } else if (data.status === 'no_auth' || data.copy_fallback) {
          // Restore item since we didn't actually post
          if (itemToRemove) {
            setPendingTweets(prev => [...prev, itemToRemove].sort((a, b) => 
              new Date(a.queued_at || 0) - new Date(b.queued_at || 0)
            ));
          }
          await navigator.clipboard.writeText(text);
          showResultModal('copied', 'Copied to Clipboard', data.message || 'Post manually and confirm with "Copied & Posted".');
        } else if (data.status === 'error') {
          // Restore item on error
          if (itemToRemove) {
            setPendingTweets(prev => [...prev, itemToRemove].sort((a, b) => 
              new Date(a.queued_at || 0) - new Date(b.queued_at || 0)
            ));
          }
          showResultModal('error', 'Failed to Post', data.message || 'Something went wrong. Try again or copy & post manually.');
        }
      }
    } catch (error) {
      console.error('Post failed:', error);
      // Restore item on network error
      if (itemToRemove && directPost) {
        setPendingTweets(prev => [...prev, itemToRemove].sort((a, b) => 
          new Date(a.queued_at || 0) - new Date(b.queued_at || 0)
        ));
      }
      showResultModal('error', 'Error', 'Network error. Please check your connection and try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const markCopied = async (queueId, text) => {
    const authHeaders = getAuthHeaders();
    setActionLoading(`mark_${queueId}`);
    try {
      await fetch(`${BACKEND_URL}/api/twitter/queue/${queueId}/copied`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({ response_text: text })
      });
      fetchData();
    } catch (error) {
      console.error('Mark copied failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const saveForLater = async (queueId) => {
    const authHeaders = getAuthHeaders();
    setActionLoading(`save_${queueId}`);
    try {
      await fetch(`${BACKEND_URL}/api/twitter/queue/${queueId}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({ note: null })
      });
      fetchData();
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const skipTweet = async (queueId) => {
    const authHeaders = getAuthHeaders();
    setActionLoading(`skip_${queueId}`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/twitter/queue/${queueId}/skip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({ reason: null })
      });
      if (res.ok) {
        // Optimistically remove from local state instead of full refetch
        setPendingTweets(prev => prev.filter(item => item.queue_id !== queueId));
      } else {
        // Only refetch on error to sync state
        fetchData();
      }
    } catch (error) {
      console.error('Skip failed:', error);
      fetchData(); // Refetch to sync state on error
    } finally {
      setActionLoading(null);
    }
  };

  const regenerateResponses = async (queueId) => {
    const authHeaders = getAuthHeaders();
    setActionLoading(`regen_${queueId}`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/twitter/queue/${queueId}/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        // Build detailed message based on what was refreshed
        let message = 'New response options have been generated.';
        const details = [];
        
        if (data.text_refreshed) {
          details.push(`Tweet text updated (${data.text_length} chars)`);
        }
        if (data.evaluation_refreshed) {
          details.push(`Re-evaluated: ${data.thesis_alignment || 'N/A'}`);
        }
        
        if (details.length > 0) {
          message = details.join(' • ') + '\n\nNew drafts generated with full context.';
        }
        
        showResultModal('success', 'Regeneration Complete', message);
      } else {
        const error = await res.json();
        showResultModal('error', 'Regeneration Failed', error.detail || 'Could not regenerate responses.');
      }
      fetchData();
    } catch (error) {
      console.error('Regenerate failed:', error);
      showResultModal('error', 'Error', 'Network error while regenerating responses.');
    } finally {
      setActionLoading(null);
    }
  };

  const refreshTweetText = async (queueId) => {
    const authHeaders = getAuthHeaders();
    setActionLoading(`refresh_${queueId}`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/twitter/queue/${queueId}/refresh-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.text_changed) {
          showResultModal('success', 'Tweet Text Updated', `Full text loaded (${data.new_length} characters).`);
        } else {
          showResultModal('info', 'No Change', 'Tweet text is already complete.');
        }
        fetchData();
      } else {
        const error = await res.json();
        showResultModal('error', 'Refresh Failed', error.detail || 'Could not refresh tweet text.');
      }
    } catch (error) {
      console.error('Refresh failed:', error);
      showResultModal('error', 'Error', 'Network error while refreshing tweet text.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCandidate = async (candidateId, action, makeSeed = false) => {
    const authHeaders = getAuthHeaders();
    setActionLoading(candidateId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/sentinel/v2/candidates/${candidateId}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({ action: action, add_as_seed: makeSeed })
      });
      if (!response.ok) {
        const err = await response.json();
        console.error('Candidate action failed:', err.detail);
      }
      fetchData();
    } catch (error) {
      console.error('Candidate action failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopiedText(id);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const connectTwitter = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const res = await fetch(`${BACKEND_URL}/api/twitter/auth/connect`, {
        credentials: 'include',
        headers: authHeaders
      });
      if (res.ok) {
        const data = await res.json();
        if (data.auth_url) {
          window.location.href = data.auth_url;
        }
      } else {
        console.error('Failed to start Twitter auth');
      }
    } catch (error) {
      console.error('Twitter auth error:', error);
    }
  };

  const disconnectTwitter = async () => {
    // Note: confirm() doesn't work in sandboxed iframes, so we just disconnect directly
    setActionLoading('disconnect_twitter');
    try {
      const authHeaders = getAuthHeaders();
      const res = await fetch(`${BACKEND_URL}/api/twitter/auth/disconnect`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders
      });
      if (res.ok) {
        // Immediately update local state for responsive UI
        setTwitterAuth(null);
        fetchData(); // Refresh to sync
      } else {
        console.error('Failed to disconnect Twitter');
      }
    } catch (error) {
      console.error('Twitter disconnect error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Manual trigger functions
  const runDiscovery = async () => {
    const authHeaders = getAuthHeaders();
    setActionLoading('discovery');
    try {
      const res = await fetch(`${BACKEND_URL}/api/sentinel/run/discovery`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders
      });
      if (res.ok) {
        // Refresh data after a delay to let the job start
        setTimeout(fetchData, 2000);
      } else {
        const err = await res.json();
        console.error('Discovery failed:', err.detail);
      }
    } catch (error) {
      console.error('Discovery failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const runMonitoring = async () => {
    const authHeaders = getAuthHeaders();
    setActionLoading('monitoring');
    try {
      const res = await fetch(`${BACKEND_URL}/api/sentinel/run/monitoring`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders
      });
      if (res.ok) {
        setTimeout(fetchData, 2000);
      } else {
        const err = await res.json();
        console.error('Monitoring failed:', err.detail);
      }
    } catch (error) {
      console.error('Monitoring failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const runFullCycle = async () => {
    const authHeaders = getAuthHeaders();
    setActionLoading('full_cycle');
    try {
      const res = await fetch(`${BACKEND_URL}/api/sentinel/run/full-cycle`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders
      });
      if (res.ok) {
        setTimeout(fetchData, 2000);
      } else {
        const err = await res.json();
        console.error('Full cycle failed:', err.detail);
      }
    } catch (error) {
      console.error('Full cycle failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Agent Start/Stop Controls
  const startAgent = async () => {
    const authHeaders = getAuthHeaders();
    setActionLoading('start_agent');
    try {
      const res = await fetch(`${BACKEND_URL}/api/sentinel/v2/agent/start`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders
      });
      if (res.ok) {
        // Immediately update local state for responsive UI
        setAgentStatus(prev => ({ ...prev, agent_enabled: true }));
        fetchData(); // Then sync with backend
      } else {
        const err = await res.json();
        console.error('Start agent failed:', err.detail);
      }
    } catch (error) {
      console.error('Start agent failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const stopAgent = async () => {
    const authHeaders = getAuthHeaders();
    setActionLoading('stop_agent');
    try {
      const res = await fetch(`${BACKEND_URL}/api/sentinel/v2/agent/stop`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders
      });
      if (res.ok) {
        // Immediately update local state for responsive UI
        setAgentStatus(prev => ({ ...prev, agent_enabled: false }));
        fetchData(); // Then sync with backend
      } else {
        const err = await res.json();
        console.error('Stop agent failed:', err.detail);
      }
    } catch (error) {
      console.error('Stop agent failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Watchlist actions
  const syncFollowing = async () => {
    const authHeaders = getAuthHeaders();
    setActionLoading('sync');
    try {
      const res = await fetch(`${BACKEND_URL}/api/sentinel/v2/sync-following`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include'
      });
      const data = await res.json();
      if (res.ok) {
        setTimeout(fetchData, 5000);
        setTimeout(fetchData, 15000);
        setTimeout(fetchData, 30000);
      } else {
        alert(data.detail || 'Failed to sync following');
      }
    } catch (error) {
      console.error('Sync following failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const addToWatchlist = async () => {
    if (!newHandle.trim()) return;
    const authHeaders = getAuthHeaders();
    setActionLoading('add_watchlist');
    try {
      const res = await fetch(`${BACKEND_URL}/api/sentinel/v2/watchlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({ screen_name: newHandle })
      });
      if (res.ok) {
        setNewHandle('');
        setTimeout(fetchData, 1000);
        setTimeout(fetchData, 5000);
      }
    } catch (error) {
      console.error('Add to watchlist failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const removeFromWatchlist = async (entryId) => {
    const authHeaders = getAuthHeaders();
    setActionLoading(`remove_${entryId}`);
    try {
      await fetch(`${BACKEND_URL}/api/sentinel/v2/watchlist/${entryId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: authHeaders
      });
      fetchData();
      setSelectedEntry(null);
    } catch (error) {
      console.error('Remove failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Helper to format time ago
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  // Get contextual empty state message for tweets
  const getTweetsEmptyState = () => {
    if (!scanStats) {
      return { title: 'No tweets waiting', subtitle: 'Agent is monitoring your watchlist automatically' };
    }
    
    const { monitoring, counts, next_run_minutes } = scanStats;
    const lastRun = formatTimeAgo(monitoring?.last_run);
    
    // No watchlist
    if (counts?.watchlist === 0) {
      return {
        title: 'No accounts to monitor',
        subtitle: 'Add accounts to your watchlist to start finding relevant tweets',
        action: 'watchlist'
      };
    }
    
    // Never run
    if (!monitoring?.last_run) {
      return {
        title: 'Waiting for first scan',
        subtitle: `Monitoring ${counts?.watchlist || 0} accounts • Will scan soon`,
        action: 'run'
      };
    }
    
    // Scanned but nothing relevant
    if (monitoring?.tweets_scanned > 0 && monitoring?.tweets_relevant === 0) {
      return {
        title: 'Scanned but nothing relevant',
        subtitle: `Last scan: ${lastRun} • Checked ${monitoring.tweets_scanned} tweets • None met thesis threshold`,
      };
    }
    
    // All caught up
    if (monitoring?.status === 'completed') {
      return {
        title: "You're all caught up",
        subtitle: `Last scan: ${lastRun} • Found ${monitoring.drafts_generated || 0} drafts • Next scan in ~${next_run_minutes} min`,
      };
    }
    
    // Default
    return {
      title: 'No tweets waiting',
      subtitle: `Monitoring ${counts?.watchlist || 0} accounts • Last scan: ${lastRun || 'never'}`,
    };
  };

  // Get contextual empty state message for accounts
  const getAccountsEmptyState = () => {
    if (!scanStats) {
      return { title: 'No accounts to review', subtitle: 'High-scoring accounts are auto-added to watchlist' };
    }
    
    const { discovery, counts, next_run_minutes } = scanStats;
    const lastRun = formatTimeAgo(discovery?.last_run);
    
    // No seeds for discovery
    if (counts?.seeds === 0 && counts?.manual === 0) {
      return {
        title: 'No seed accounts for discovery',
        subtitle: 'Add accounts manually to start network crawling',
        action: 'watchlist'
      };
    }
    
    // Never run
    if (!discovery?.last_run) {
      return {
        title: 'Waiting for first discovery',
        subtitle: `Will crawl ${counts?.seeds || 0} seed accounts soon`,
        action: 'run'
      };
    }
    
    // All auto-processed
    if (discovery?.candidates_found > 0 && discovery?.candidates_queued === 0) {
      const autoAdded = discovery?.candidates_auto_added || 0;
      const rejected = discovery?.candidates_rejected || 0;
      return {
        title: 'All candidates auto-processed',
        subtitle: `Last discovery: ${lastRun} • Found ${discovery.candidates_found} • ${autoAdded} auto-added, ${rejected} auto-rejected`,
      };
    }
    
    // Nothing found
    if (discovery?.candidates_found === 0) {
      return {
        title: 'No new accounts found',
        subtitle: `Last discovery: ${lastRun} • Network already saturated or accounts inactive`,
      };
    }
    
    // All reviewed
    if (discovery?.status === 'completed') {
      return {
        title: "You've reviewed all candidates",
        subtitle: `Last discovery: ${lastRun} • Next discovery in ~${next_run_minutes} min`,
      };
    }
    
    // Default
    return {
      title: 'No accounts to review',
      subtitle: `Last discovery: ${lastRun || 'never'} • High-scoring accounts are auto-added`,
    };
  };

  // ============ RENDER ============
  
  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-full mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="font-mono text-lg tracking-widest text-[#F3F4F6]">
              WYCO<span className="text-[#00F0FF]">CAPITAL</span>
            </a>
            <span className="font-mono text-xs text-[#00F0FF] border-l border-white/10 pl-4">
              SOCIAL SENTINEL
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Manual Trigger Buttons */}
            <div className="flex items-center gap-1 border-r border-white/10 pr-4">
              <Button
                size="sm"
                onClick={runFullCycle}
                disabled={agentStatus.status === 'running' || !!actionLoading}
                className="bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 hover:bg-[#00F0FF]/20 disabled:opacity-50"
                title="Run full cycle (discovery + monitoring)"
              >
                {actionLoading === 'full_cycle' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={runDiscovery}
                disabled={agentStatus.status === 'running' || !!actionLoading}
                className="text-[#6B7280] hover:text-[#F3F4F6] disabled:opacity-50"
                title="Run discovery only"
              >
                {actionLoading === 'discovery' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Users className="w-4 h-4" />
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={runMonitoring}
                disabled={agentStatus.status === 'running' || !!actionLoading}
                className="text-[#6B7280] hover:text-[#F3F4F6] disabled:opacity-50"
                title="Run monitoring only"
              >
                {actionLoading === 'monitoring' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            {/* Agent Control & Status */}
            <div className="flex items-center gap-2 border-r border-white/10 pr-4">
              {/* Start/Stop Buttons */}
              {settings?.agent_enabled !== false && agentStatus.agent_enabled !== false ? (
                <Button
                  size="sm"
                  onClick={stopAgent}
                  disabled={!!actionLoading}
                  className="bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 disabled:opacity-50"
                  title="Stop agent"
                >
                  {actionLoading === 'stop_agent' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Pause className="w-4 h-4" />
                  )}
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={startAgent}
                  disabled={!!actionLoading}
                  className="bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20 disabled:opacity-50"
                  title="Start agent"
                >
                  {actionLoading === 'start_agent' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Power className="w-4 h-4" />
                  )}
                </Button>
              )}
              
              {/* Agent Status Indicator - Clickable to force immediate run */}
              <div 
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all ${
                  agentStatus.agent_enabled === false 
                    ? 'bg-red-500/10 border-red-500/30' 
                    : agentStatus.auto_paused 
                      ? 'bg-orange-500/10 border-orange-500/30' 
                      : agentStatus.status === 'running'
                        ? 'bg-green-500/10 border-green-500/30'
                        : 'bg-white/5 border-white/10 hover:border-[#00F0FF]/50 hover:bg-[#00F0FF]/10 cursor-pointer'
                }`}
                onClick={() => {
                  // Only allow click when showing "Next: Xm" (idle state, agent enabled)
                  if (agentStatus.agent_enabled !== false && 
                      !agentStatus.auto_paused && 
                      agentStatus.status !== 'running') {
                    runFullCycle();
                  }
                }}
                title={agentStatus.status !== 'running' && agentStatus.agent_enabled !== false && !agentStatus.auto_paused 
                  ? 'Click to run now' 
                  : undefined}
              >
                <span className={`w-2 h-2 rounded-full ${
                  agentStatus.agent_enabled === false ? 'bg-red-400' :
                  agentStatus.auto_paused ? 'bg-orange-400' :
                  agentStatus.status === 'running' ? 'bg-green-400 animate-pulse' :
                  agentStatus.status === 'error' ? 'bg-red-400' : 'bg-yellow-400'
                }`} />
                <span className={`font-mono text-xs ${
                  agentStatus.agent_enabled === false ? 'text-red-400' :
                  agentStatus.auto_paused ? 'text-orange-400' : 'text-[#F3F4F6]'
                }`}>
                  {agentStatus.agent_enabled === false ? 'Stopped' :
                   agentStatus.auto_paused ? 'Paused (inactive)' :
                   agentStatus.status === 'running' ? 'Running' : 
                   `Next: ${agentStatus.next_run_minutes}m`}
                </span>
              </div>
            </div>
            
            {/* Quick Console Links */}
            <div className="flex items-center gap-1">
              <a
                href="https://console.x.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[#00F0FF]/30 transition-colors"
                title="xAI / Grok Console"
              >
                <span className="text-xs">🤖</span>
                <span className="font-mono text-[10px] text-[#6B7280] hover:text-[#F3F4F6]">xAI</span>
              </a>
              <a
                href="https://console.cloud.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[#00F0FF]/30 transition-colors"
                title="Google Cloud Console"
              >
                <span className="text-xs">☁️</span>
                <span className="font-mono text-[10px] text-[#6B7280] hover:text-[#F3F4F6]">GCP</span>
              </a>
            </div>
            
            {/* Twitter Status */}
            {twitterAuth?.connected ? (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/30">
                  <Twitter className="w-4 h-4 text-green-400" />
                  <span className="font-mono text-xs text-green-400">@{twitterAuth.twitter_username}</span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={disconnectTwitter}
                  disabled={actionLoading === 'disconnect_twitter'}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 px-2"
                  title="Disconnect X account"
                >
                  {actionLoading === 'disconnect_twitter' ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : (
                    <X className="w-3 h-3" />
                  )}
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                onClick={connectTwitter}
                className="bg-[#1DA1F2]/10 text-[#1DA1F2] border border-[#1DA1F2]/30 hover:bg-[#1DA1F2]/20"
              >
                <Twitter className="w-4 h-4 mr-1" />
                Connect X
              </Button>
            )}
            
            <span className="font-sans text-sm text-[#6B7280]">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={logout} className="text-[#6B7280] hover:text-[#F3F4F6]">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Main Content */}
        <main className={`flex-1 px-6 py-6 transition-all ${activityPanelCollapsed ? 'mr-12' : 'mr-80'}`}>
          {/* Tabbed Decision Queues */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-white/[0.02] border border-white/10 p-1 mb-6 flex-wrap">
              <TabsTrigger 
                value="compose" 
                className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400 text-[#6B7280] font-mono text-sm px-4 py-2"
              >
                <Zap className="w-4 h-4 mr-2" />
                COMPOSE
              </TabsTrigger>
              <TabsTrigger 
                value="tweets" 
                className="data-[state=active]:bg-[#00F0FF]/10 data-[state=active]:text-[#00F0FF] text-[#6B7280] font-mono text-sm px-4 py-2"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                TWEETS
                {pendingTweets.length > 0 && (
                  <Badge className="ml-2 bg-[#00F0FF]/20 text-[#00F0FF] text-xs">{pendingTweets.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="accounts" 
                className="data-[state=active]:bg-orange-500/10 data-[state=active]:text-orange-400 text-[#6B7280] font-mono text-sm px-4 py-2"
              >
                <Users className="w-4 h-4 mr-2" />
                ACCOUNTS
                {pendingAccounts.length > 0 && (
                  <Badge className="ml-2 bg-orange-500/20 text-orange-400 text-xs">{pendingAccounts.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="watchlist" 
                className="data-[state=active]:bg-green-500/10 data-[state=active]:text-green-400 text-[#6B7280] font-mono text-sm px-4 py-2"
              >
                <Eye className="w-4 h-4 mr-2" />
                WATCHLIST
                <Badge className="ml-2 bg-green-500/20 text-green-400 text-xs">{watchlist.length}</Badge>
              </TabsTrigger>
              <TabsTrigger 
                value="saved" 
                className="data-[state=active]:bg-yellow-500/10 data-[state=active]:text-yellow-400 text-[#6B7280] font-mono text-sm px-4 py-2"
              >
                <Bookmark className="w-4 h-4 mr-2" />
                SAVED
                {savedTweets.length > 0 && (
                  <Badge className="ml-2 bg-yellow-500/20 text-yellow-400 text-xs">{savedTweets.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="responses" 
                className="data-[state=active]:bg-blue-500/10 data-[state=active]:text-blue-400 text-[#6B7280] font-mono text-sm px-4 py-2"
              >
                <Send className="w-4 h-4 mr-2" />
                RESPONSES
                {responses.length > 0 && (
                  <Badge className="ml-2 bg-blue-500/20 text-blue-400 text-xs">{responses.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="analytics" 
                className="data-[state=active]:bg-purple-500/10 data-[state=active]:text-purple-400 text-[#6B7280] font-mono text-sm px-4 py-2"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                ANALYTICS
              </TabsTrigger>
              <TabsTrigger 
                value="settings" 
                className="data-[state=active]:bg-gray-500/10 data-[state=active]:text-gray-300 text-[#6B7280] font-mono text-sm px-4 py-2"
              >
                <Settings className="w-4 h-4 mr-2" />
                SETTINGS
              </TabsTrigger>
            </TabsList>

            {/* Compose Tab Content */}
            <TabsContent value="compose" className="mt-0">
              <ComposeTab 
                getAuthHeaders={getAuthHeaders}
                twitterConnected={twitterAuth?.connected}
                settings={settings}
              />
            </TabsContent>

            {/* Tweets Tab Content */}
            <TabsContent value="tweets" className="mt-0">
              {pendingTweets.length === 0 ? (
                (() => {
                  const emptyState = getTweetsEmptyState();
                  return (
                    <Card className="bg-white/[0.02] border-white/10">
                      <CardContent className="py-12 text-center">
                        <MessageSquare className="w-10 h-10 text-[#6B7280]/50 mx-auto mb-4" />
                        <p className="font-sans text-base text-[#F3F4F6]/80">{emptyState.title}</p>
                        <p className="font-sans text-sm text-[#6B7280] mt-2 max-w-md mx-auto">
                          {emptyState.subtitle}
                        </p>
                        {emptyState.action === 'watchlist' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-6 border-[#00F0FF]/30 text-[#00F0FF]"
                            onClick={() => setActiveTab('watchlist')}
                          >
                            Go to Watchlist
                          </Button>
                        )}
                        {emptyState.action === 'run' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-6 border-[#00F0FF]/30 text-[#00F0FF]"
                            onClick={runMonitoring}
                            disabled={actionLoading === 'monitoring'}
                          >
                            {actionLoading === 'monitoring' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                            Run Scan Now
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()
              ) : (
                <div className="space-y-4">
                  {pendingTweets.map(item => (
                    <TweetCard
                      key={item.queue_id}
                      item={item}
                      onPost={postResponse}
                      onSkip={skipTweet}
                      onCopy={copyToClipboard}
                      onMarkCopied={markCopied}
                      onSave={saveForLater}
                      onRegenerate={regenerateResponses}
                      onRefreshText={refreshTweetText}
                      actionLoading={actionLoading}
                      copiedText={copiedText}
                      twitterConnected={twitterAuth?.connected}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Accounts Tab Content */}
            <TabsContent value="accounts" className="mt-0">
              {pendingAccounts.length === 0 ? (
                (() => {
                  const emptyState = getAccountsEmptyState();
                  return (
                    <Card className="bg-white/[0.02] border-white/10">
                      <CardContent className="py-12 text-center">
                        <Users className="w-10 h-10 text-[#6B7280]/50 mx-auto mb-4" />
                        <p className="font-sans text-base text-[#F3F4F6]/80">{emptyState.title}</p>
                        <p className="font-sans text-sm text-[#6B7280] mt-2 max-w-md mx-auto">
                          {emptyState.subtitle}
                        </p>
                        {emptyState.action === 'watchlist' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-6 border-orange-500/30 text-orange-400"
                            onClick={() => setActiveTab('watchlist')}
                          >
                            Add Accounts
                          </Button>
                        )}
                        {emptyState.action === 'run' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-6 border-orange-500/30 text-orange-400"
                            onClick={runDiscovery}
                            disabled={actionLoading === 'discovery'}
                          >
                            {actionLoading === 'discovery' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                            Run Discovery Now
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {pendingAccounts.map(candidate => (
                    <CandidateCard
                      key={candidate.candidate_id}
                      candidate={candidate}
                      onAction={handleCandidate}
                      actionLoading={actionLoading}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Analytics Tab Content */}
            <TabsContent value="analytics" className="mt-0">
              <AnalyticsTab />
            </TabsContent>

            {/* Watchlist Tab Content */}
            <TabsContent value="watchlist" className="mt-0">
              <WatchlistTabContent
                watchlist={watchlist}
                watchlistFilter={watchlistFilter}
                setWatchlistFilter={setWatchlistFilter}
                newHandle={newHandle}
                setNewHandle={setNewHandle}
                addToWatchlist={addToWatchlist}
                removeFromWatchlist={removeFromWatchlist}
                syncFollowing={syncFollowing}
                actionLoading={actionLoading}
                selectedEntry={selectedEntry}
                setSelectedEntry={setSelectedEntry}
                getAuthHeaders={getAuthHeaders}
                fetchData={fetchData}
              />
            </TabsContent>

            {/* Saved Tab Content */}
            <TabsContent value="saved" className="mt-0">
              <SavedTabContent
                savedTweets={savedTweets}
                getAuthHeaders={getAuthHeaders}
                fetchData={fetchData}
              />
            </TabsContent>

            {/* Responses Tab Content */}
            <TabsContent value="responses" className="mt-0">
              <ResponsesTabContent
                responses={responses}
                getAuthHeaders={getAuthHeaders}
                fetchData={fetchData}
              />
            </TabsContent>

            {/* Settings Tab Content */}
            <TabsContent value="settings" className="mt-0">
              <SettingsTabContent
                settings={settings}
                getAuthHeaders={getAuthHeaders}
                fetchData={fetchData}
              />
            </TabsContent>
          </Tabs>
        </main>

        {/* Activity Panel */}
        <ActivityPanel
          activities={activities}
          collapsed={activityPanelCollapsed}
          onToggle={() => setActivityPanelCollapsed(!activityPanelCollapsed)}
          onRefresh={fetchActivities}
          agentStatus={agentStatus}
          connected={sseConnected}
        />
      </div>

      {/* Twitter Connection Modal */}
      <Dialog open={showTwitterModal} onOpenChange={(open) => !open && dismissTwitterModal()}>
        <DialogContent className="max-w-md bg-[#0a0a0a] border-white/10 text-[#F3F4F6]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="p-2 rounded-full bg-[#1DA1F2]/10">
                <Twitter className="w-6 h-6 text-[#1DA1F2]" />
              </div>
              Connect Your X Account
            </DialogTitle>
            <DialogDescription className="text-[#6B7280] pt-2">
              Unlock powerful features and save on API costs
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-green-500/5 border border-green-500/20">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                <div>
                  <p className="font-sans text-sm text-[#F3F4F6]">Free Profile Fetches</p>
                  <p className="font-sans text-xs text-[#6B7280]">Use Twitter API instead of Grok — saves ~$30/day</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
                <CheckCircle className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" />
                <div>
                  <p className="font-sans text-sm text-[#F3F4F6]">Post Responses Directly</p>
                  <p className="font-sans text-xs text-[#6B7280]">Reply to tweets without leaving the dashboard</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 rounded-lg bg-purple-500/5 border border-purple-500/20">
                <CheckCircle className="w-5 h-5 text-purple-400 mt-0.5 shrink-0" />
                <div>
                  <p className="font-sans text-sm text-[#F3F4F6]">Track Engagement</p>
                  <p className="font-sans text-xs text-[#6B7280]">Monitor likes, replies, and retweets on your responses</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
                <CheckCircle className="w-5 h-5 text-yellow-400 mt-0.5 shrink-0" />
                <div>
                  <p className="font-sans text-sm text-[#F3F4F6]">Sync Your Following</p>
                  <p className="font-sans text-xs text-[#6B7280]">Import and evaluate accounts you already follow</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={dismissTwitterModal}
              className="flex-1 text-[#6B7280] hover:text-[#F3F4F6]"
            >
              Maybe Later
            </Button>
            <Button
              onClick={() => {
                connectTwitter();
                setShowTwitterModal(false);
              }}
              className="flex-1 bg-[#1DA1F2] text-white hover:bg-[#1DA1F2]/90"
            >
              <Twitter className="w-4 h-4 mr-2" />
              Connect X Account
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Result Modal */}
      {resultModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeResultModal} />
          <div className="relative bg-[#0A0A0A] border border-white/10 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <button
              onClick={closeResultModal}
              className="absolute top-4 right-4 text-[#6B7280] hover:text-[#F3F4F6] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-center">
              {resultModal.type === 'success' ? (
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              ) : resultModal.type === 'copied' ? (
                <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                  <Copy className="w-8 h-8 text-blue-400" />
                </div>
              ) : resultModal.type === 'info' ? (
                <div className="w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-cyan-400" />
                </div>
              ) : (
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-red-400" />
                </div>
              )}
              <h3 className="text-xl font-semibold text-[#F3F4F6] mb-2">{resultModal.title}</h3>
              <p className="text-[#6B7280] text-sm mb-6">{resultModal.message}</p>
              {resultModal.tweetUrl && (
                <a
                  href={resultModal.tweetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#00F0FF]/10 text-[#00F0FF] rounded-lg border border-[#00F0FF]/30 hover:bg-[#00F0FF]/20 transition-colors mb-4"
                >
                  <ExternalLink className="w-4 h-4" />
                  View on X
                </a>
              )}
              <div className="mt-4">
                <Button
                  onClick={closeResultModal}
                  className="w-full bg-white/10 text-[#F3F4F6] hover:bg-white/20"
                >
                  {resultModal.type === 'success' ? 'Done' : 'Close'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ============ TWEET CARD ============
const TweetCard = ({ item, onPost, onSkip, onCopy, onMarkCopied, onSave, onRegenerate, onRefreshText, actionLoading, copiedText, twitterConnected }) => {
  const tweet = item.tweet || {};
  const drafts = item.drafts || [];
  const evaluation = tweet.evaluation || {};
  const [showFullTweet, setShowFullTweet] = useState(false);
  
  const isLoading = actionLoading && (
    actionLoading.startsWith(`post_${item.queue_id}`) ||
    actionLoading === `mark_${item.queue_id}` ||
    actionLoading === `save_${item.queue_id}` ||
    actionLoading === `skip_${item.queue_id}` ||
    actionLoading === `regen_${item.queue_id}` ||
    actionLoading === `refresh_${item.queue_id}`
  );

  // Truncate long tweets
  const tweetText = tweet.tweet_text || '';
  const isLongTweet = tweetText.length > 280;
  // Detect potentially truncated tweets (ends with "..." or exactly 280 chars, or contains truncation marker)
  const mayBeTruncated = !isLongTweet && (
    tweetText.endsWith('...') || 
    tweetText.endsWith('…') || 
    (tweetText.length >= 250 && tweetText.length <= 280)
  );
  const displayText = showFullTweet ? tweetText : (isLongTweet ? tweetText.slice(0, 280) + '...' : tweetText);

  return (
    <Card className={`bg-white/[0.03] border-white/10 ${isLoading ? 'opacity-75' : ''}`}>
      <CardContent className="p-5">
        {/* Tweet Header */}
        <div className="flex items-start justify-between mb-3">
          <a
            href={tweet.tweet_url || `https://x.com/${tweet.author_handle}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-[#00F0FF] hover:underline flex items-center gap-1"
          >
            @{tweet.author_handle}
            <ExternalLink className="w-3 h-3" />
          </a>
          <div className="flex items-center gap-2">
            {evaluation.thesis_alignment && (
              <Badge variant="outline" className={`text-xs ${
                evaluation.thesis_alignment === 'strong' ? 'text-green-400 border-green-400/30' :
                evaluation.thesis_alignment === 'moderate' ? 'text-yellow-400 border-yellow-400/30' :
                evaluation.thesis_alignment === 'tension' ? 'text-orange-400 border-orange-400/30' :
                'text-[#6B7280] border-white/10'
              }`}>
                {evaluation.thesis_alignment}
              </Badge>
            )}
            {evaluation.category && evaluation.category !== 'Off-topic' && (
              <Badge variant="outline" className="text-xs text-[#6B7280] border-white/10">
                {evaluation.category === 'Efficiency' ? <Zap className="w-3 h-3 mr-1" /> : <Brain className="w-3 h-3 mr-1" />}
                {evaluation.category}
              </Badge>
            )}
          </div>
        </div>

        {/* Tweet Text with Show More/Less */}
        <div className="mb-4">
          <p className="font-sans text-[#F3F4F6] leading-relaxed">{displayText}</p>
          <div className="flex items-center gap-3 mt-1">
            {isLongTweet && (
              <button
                onClick={() => setShowFullTweet(!showFullTweet)}
                className="text-[#00F0FF] text-sm hover:underline"
              >
                {showFullTweet ? 'Show less' : 'Show more'}
              </button>
            )}
            {mayBeTruncated && onRefreshText && (
              <button
                onClick={() => onRefreshText(item.queue_id)}
                disabled={actionLoading === `refresh_${item.queue_id}`}
                className="text-[#6B7280] text-sm hover:text-[#00F0FF] hover:underline flex items-center gap-1 disabled:opacity-50"
                title="Tweet may be truncated. Click to fetch full text from X."
              >
                <RefreshCw className={`w-3 h-3 ${actionLoading === `refresh_${item.queue_id}` ? 'animate-spin' : ''}`} />
                {actionLoading === `refresh_${item.queue_id}` ? 'Loading...' : 'Load full text'}
              </button>
            )}
          </div>
        </div>

        {/* AI Reasoning */}
        {(evaluation.reasoning || tweet.relevance_summary) && (
          <div className="p-3 rounded bg-white/[0.02] border border-white/5 mb-4">
            <p className="font-sans text-xs text-[#6B7280]">
              <span className="text-[#00F0FF]">AI:</span> {evaluation.reasoning || tweet.relevance_summary}
            </p>
          </div>
        )}

        {/* Response Options */}
        <div className="space-y-2">
          {drafts.map(draft => (
            <div
              key={draft.draft_id}
              className="p-3 rounded bg-white/[0.02] border border-white/5 hover:border-[#00F0FF]/30 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <Badge
                    variant="outline"
                    className={`mb-2 text-xs ${
                      draft.option_type === 'agree_amplify' ? 'text-green-400 border-green-400/30' :
                      draft.option_type === 'nuanced_pushback' ? 'text-orange-400 border-orange-400/30' :
                      'text-cyan-400 border-cyan-400/30'
                    }`}
                  >
                    {draft.option_label}
                  </Badge>
                  <p className="font-sans text-sm text-[#F3F4F6]">{draft.draft_text}</p>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    onClick={() => onPost(item.queue_id, draft.draft_id, draft.draft_text, true)}
                    disabled={isLoading}
                    className="bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 hover:bg-[#00F0FF]/20 disabled:opacity-50"
                  >
                    {actionLoading === `post_${item.queue_id}_${draft.draft_id}` ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : twitterConnected ? (
                      <Send className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onCopy(draft.draft_text, draft.draft_id)}
                    className="text-[#6B7280] hover:text-[#F3F4F6]"
                  >
                    {copiedText === draft.draft_id ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="mt-4 pt-3 border-t border-white/5 flex justify-between items-center">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onMarkCopied(item.queue_id, drafts[0]?.draft_text || '')}
              disabled={isLoading}
              className="border-green-500/30 text-green-400 hover:bg-green-500/10 disabled:opacity-50"
            >
              {actionLoading === `mark_${item.queue_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              <span className="ml-1 hidden sm:inline">Posted</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSave(item.queue_id)}
              disabled={isLoading}
              className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 disabled:opacity-50"
            >
              {actionLoading === `save_${item.queue_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bookmark className="w-4 h-4" />}
              <span className="ml-1 hidden sm:inline">Save</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSkip(item.queue_id)}
              disabled={isLoading}
              className="text-[#6B7280] hover:text-[#F3F4F6] disabled:opacity-50"
            >
              {actionLoading === `skip_${item.queue_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <SkipForward className="w-4 h-4" />}
              <span className="ml-1 hidden sm:inline">Skip</span>
            </Button>
          </div>
          {/* Regenerate Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRegenerate(item.queue_id)}
            disabled={isLoading}
            className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 disabled:opacity-50"
          >
            {actionLoading === `regen_${item.queue_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-1 hidden sm:inline">Regenerate</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


// ============ CANDIDATE CARD ============
const CandidateCard = ({ candidate, onAction, actionLoading }) => {
  const evaluation = candidate.evaluation || {};
  const isLoading = actionLoading === candidate.candidate_id;

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-green-400 border-green-400/30';
    if (score >= 50) return 'text-yellow-400 border-yellow-400/30';
    return 'text-red-400 border-red-400/30';
  };

  return (
    <Card className={`bg-white/[0.03] border-white/10 ${isLoading ? 'opacity-75' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <a
                href={`https://x.com/${candidate.screen_name}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[#F3F4F6] hover:text-[#00F0FF] flex items-center gap-1"
              >
                @{candidate.screen_name}
                <ExternalLink className="w-3 h-3" />
              </a>
              <Badge variant="outline" className={`font-mono text-xs ${getScoreColor(evaluation.relevance_score || 0)}`}>
                {evaluation.relevance_score || 0}
              </Badge>
            </div>
            
            {candidate.bio && (
              <p className="font-sans text-xs text-[#6B7280] mb-2 line-clamp-2">{candidate.bio}</p>
            )}
            
            <p className="font-sans text-xs text-[#F3F4F6]/80">
              <span className="text-[#00F0FF]">AI:</span> {evaluation.reasoning_summary || 'No analysis'}
            </p>
            
            {candidate.connection_detail && (
              <p className="font-mono text-[10px] text-[#6B7280] mt-1">
                {candidate.connection_detail}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1 ml-3">
            <Button
              size="sm"
              onClick={() => onAction(candidate.candidate_id, 'approve', false)}
              disabled={isLoading}
              className="bg-green-500/10 text-green-400 border border-green-500/30 disabled:opacity-50"
              title="Add to watchlist (auto-follows on X if connected)"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            </Button>
            <Button
              size="sm"
              onClick={() => onAction(candidate.candidate_id, 'approve', true)}
              disabled={isLoading}
              className="bg-orange-500/10 text-orange-400 border border-orange-500/30 disabled:opacity-50"
              title="Add to watchlist as seed account (for network crawling)"
            >
              <Star className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onAction(candidate.candidate_id, 'reject')}
              disabled={isLoading}
              className="text-red-400 disabled:opacity-50"
              title="Reject candidate"
            >
              <XCircle className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


// ============ ACTIVITY PANEL ============
const ActivityPanel = ({ activities, collapsed, onToggle, onRefresh, agentStatus, connected }) => {
  const scrollRef = useRef(null);

  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
    } catch {
      return '--:--';
    }
  };

  if (collapsed) {
    return (
      <div className="fixed right-0 top-20 bottom-0 w-12 bg-[#050505]/95 backdrop-blur-xl border-l border-white/10 flex flex-col items-center py-4 z-40">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className="text-[#6B7280] hover:text-[#00F0FF] p-2"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="mt-4 flex flex-col items-center gap-2">
          <Activity className={`w-4 h-4 ${agentStatus.status === 'running' ? 'text-green-400 animate-pulse' : 'text-[#6B7280]'}`} />
          <Badge className="bg-[#00F0FF]/20 text-[#00F0FF] text-xs px-1">
            {activities.length}
          </Badge>
          {connected && <span className="w-2 h-2 rounded-full bg-green-400" title="Live" />}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed right-0 top-20 bottom-0 w-80 bg-[#050505]/95 backdrop-blur-xl border-l border-white/10 flex flex-col z-40">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <Activity className={`w-4 h-4 ${agentStatus.status === 'running' ? 'text-green-400 animate-pulse' : 'text-[#6B7280]'}`} />
          <span className="font-mono text-xs text-[#F3F4F6]">AGENT ACTIVITY</span>
          {connected ? (
            <span className="w-2 h-2 rounded-full bg-green-400" title="Live connection" />
          ) : (
            <span className="font-mono text-[10px] text-yellow-400">(polling)</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            className="text-[#6B7280] hover:text-[#F3F4F6] p-1 h-auto"
          >
            <RefreshCw className="w-3 h-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className="text-[#6B7280] hover:text-[#F3F4F6] p-1 h-auto"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Activity Feed */}
      <ScrollArea className="flex-1" ref={scrollRef}>
        <div className="p-3 space-y-1">
          {activities.length === 0 ? (
            <div className="text-center py-8">
              <Activity className="w-8 h-8 text-[#6B7280]/50 mx-auto mb-2" />
              <p className="font-sans text-xs text-[#6B7280]">No activity yet</p>
            </div>
          ) : (
            activities.map((activity, idx) => (
              <div
                key={`${activity.log_id || 'activity'}-${idx}`}
                className={`p-2 rounded text-xs border-l-2 ${
                  activity.status === 'success' ? 'border-l-green-400/50 bg-green-400/5' :
                  activity.status === 'error' ? 'border-l-red-400/50 bg-red-400/5' :
                  activity.status === 'warning' ? 'border-l-yellow-400/50 bg-yellow-400/5' :
                  'border-l-[#00F0FF]/30 bg-white/[0.02]'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-sm leading-none">{activity.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-[#F3F4F6] leading-tight">{activity.message}</p>
                    {activity.reasoning && (
                      <p className="font-sans text-[10px] text-[#6B7280] mt-0.5 italic">
                        {activity.reasoning}
                      </p>
                    )}
                    {activity.details && (
                      <p className="font-mono text-[10px] text-[#6B7280] mt-0.5">
                        {activity.details}
                      </p>
                    )}
                  </div>
                  <span className="font-mono text-[10px] text-[#6B7280] shrink-0">
                    {formatTime(activity.timestamp)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="p-3 border-t border-white/10 text-center">
        <span className="font-mono text-[10px] text-[#6B7280]">
          24-hour rolling log • {activities.length} events
        </span>
      </div>
    </div>
  );
};


// ============ WATCHLIST TAB CONTENT ============
const WatchlistTabContent = ({ watchlist, watchlistFilter, setWatchlistFilter, newHandle, setNewHandle, addToWatchlist, removeFromWatchlist, syncFollowing, actionLoading, selectedEntry, setSelectedEntry, getAuthHeaders, fetchData }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [localActionLoading, setLocalActionLoading] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const triggerEvaluation = async (entryId) => {
    const authHeaders = getAuthHeaders();
    setLocalActionLoading(`eval_${entryId}`);
    try {
      await fetch(`${BACKEND_URL}/api/sentinel/v2/watchlist/${entryId}/evaluate`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders
      });
      setTimeout(fetchData, 3000);
      setTimeout(fetchData, 8000);
    } catch (error) {
      console.error('Failed to trigger evaluation:', error);
    } finally {
      setLocalActionLoading(null);
    }
  };

  const getScoreColor = (score) => {
    if (!score) return 'text-[#6B7280] border-white/10';
    if (score >= 75) return 'text-green-400 border-green-400/30';
    if (score >= 50) return 'text-yellow-400 border-yellow-400/30';
    return 'text-red-400 border-red-400/30';
  };

  const filteredWatchlist = watchlist.filter(entry => {
    if (watchlistFilter === 'manual' && entry.source !== 'manual') return false;
    if (watchlistFilter === 'discovered' && entry.source === 'manual') return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        entry.screen_name?.toLowerCase().includes(term) ||
        entry.display_name?.toLowerCase().includes(term) ||
        entry.reasoning?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  const manualCount = watchlist.filter(e => e.source === 'manual').length;
  const discoveredCount = watchlist.filter(e => e.source !== 'manual').length;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'Unknown';
    }
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex gap-2 flex-1">
          <Input
            value={newHandle}
            onChange={(e) => setNewHandle(e.target.value)}
            placeholder="@username to add directly"
            className="bg-white/5 border-white/10 text-[#F3F4F6]"
            onKeyDown={(e) => e.key === 'Enter' && addToWatchlist()}
            disabled={actionLoading === 'add_watchlist'}
          />
          <Button
            onClick={addToWatchlist}
            disabled={!newHandle.trim() || actionLoading === 'add_watchlist'}
            className="bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 hover:bg-[#00F0FF]/20"
          >
            {actionLoading === 'add_watchlist' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            className="pl-10 bg-white/5 border-white/10 text-[#F3F4F6] w-full md:w-64"
          />
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={watchlistFilter === 'all' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setWatchlistFilter('all')}
          className={watchlistFilter === 'all' ? 'bg-green-500 text-[#050505]' : 'text-[#6B7280]'}
        >
          All ({watchlist.length})
        </Button>
        <Button
          variant={watchlistFilter === 'manual' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setWatchlistFilter('manual')}
          className={watchlistFilter === 'manual' ? 'bg-cyan-500/20 text-cyan-400' : 'text-[#6B7280]'}
        >
          <Star className="w-3 h-3 mr-1" />
          Manual ({manualCount})
        </Button>
        <Button
          variant={watchlistFilter === 'discovered' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setWatchlistFilter('discovered')}
          className={watchlistFilter === 'discovered' ? 'bg-purple-500/20 text-purple-400' : 'text-[#6B7280]'}
        >
          <Search className="w-3 h-3 mr-1" />
          Discovered ({discoveredCount})
        </Button>
        <div className="flex-1" />
        <Button
          onClick={syncFollowing}
          disabled={actionLoading === 'sync'}
          size="sm"
          className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30"
        >
          {actionLoading === 'sync' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Sync Following
        </Button>
      </div>

      {/* Watchlist Grid */}
      {filteredWatchlist.length === 0 ? (
        <Card className="bg-white/[0.02] border-white/10">
          <CardContent className="py-12 text-center">
            <Users className="w-10 h-10 text-[#6B7280]/50 mx-auto mb-3" />
            <p className="font-sans text-sm text-[#6B7280]">
              {searchTerm ? 'No matching accounts' : 'Watchlist is empty'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filteredWatchlist.map(entry => (
            <Card
              key={entry.entry_id}
              className={`bg-white/[0.03] border-white/10 cursor-pointer hover:bg-white/[0.05] transition-colors ${
                entry.source === 'manual' ? 'border-l-2 border-l-cyan-500' : ''
              }`}
              onClick={() => setSelectedEntry(entry)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-sm text-[#F3F4F6] truncate">@{entry.screen_name}</span>
                      {entry.source === 'manual' && (
                        <Star className="w-3 h-3 text-cyan-400 shrink-0" fill="currentColor" />
                      )}
                    </div>
                    {entry.display_name && (
                      <p className="font-sans text-xs text-[#6B7280] truncate">{entry.display_name}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      {entry.relevance_score ? (
                        <Badge variant="outline" className={`text-xs ${getScoreColor(entry.relevance_score)}`}>
                          {entry.relevance_score}
                        </Badge>
                      ) : entry.evaluation_status === 'pending' ? (
                        <Badge variant="outline" className="text-xs text-yellow-400 border-yellow-400/30">
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Evaluating...
                        </Badge>
                      ) : null}
                      {entry.primary_category && (
                        <Badge variant="outline" className="text-xs text-[#6B7280] border-white/10">
                          {entry.primary_category === 'Efficiency' ? <Zap className="w-3 h-3 mr-1" /> : <Brain className="w-3 h-3 mr-1" />}
                          {entry.primary_category}
                        </Badge>
                      )}
                    </div>
                    {entry.reasoning && (
                      <p className="font-sans text-xs text-[#6B7280]/80 mt-2 line-clamp-2">{entry.reasoning}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                    {!entry.relevance_score && entry.evaluation_status !== 'pending' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => triggerEvaluation(entry.entry_id)}
                        disabled={localActionLoading === `eval_${entry.entry_id}`}
                        className="p-1 h-auto text-[#00F0FF]"
                      >
                        {localActionLoading === `eval_${entry.entry_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeFromWatchlist(entry.entry_id)}
                      disabled={actionLoading === `remove_${entry.entry_id}`}
                      className="p-1 h-auto text-[#6B7280] hover:text-red-400"
                    >
                      {actionLoading === `remove_${entry.entry_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={!!selectedEntry} onOpenChange={(open) => !open && setSelectedEntry(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border-white/10 text-[#F3F4F6]">
          <DialogHeader>
            <DialogTitle className="sr-only">Profile Details</DialogTitle>
            <DialogDescription className="sr-only">Detailed profile information</DialogDescription>
          </DialogHeader>
          {selectedEntry && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <a href={`https://x.com/${selectedEntry.screen_name}`} target="_blank" rel="noopener noreferrer" className="font-mono text-lg text-[#F3F4F6] hover:text-[#00F0FF] flex items-center gap-2">
                  @{selectedEntry.screen_name}
                  <ExternalLink className="w-4 h-4" />
                </a>
                {selectedEntry.source === 'manual' && (
                  <Badge className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                    <Star className="w-3 h-3 mr-1" fill="currentColor" />
                    Manual
                  </Badge>
                )}
              </div>
              {selectedEntry.display_name && <p className="font-sans text-xl text-[#F3F4F6]">{selectedEntry.display_name}</p>}
              {selectedEntry.bio && <p className="font-sans text-sm text-[#6B7280]">{selectedEntry.bio}</p>}
              {selectedEntry.relevance_score !== undefined && selectedEntry.relevance_score !== null && (
                <div className="flex items-center gap-3 flex-wrap">
                  <div className={`px-4 py-2 rounded-lg font-mono text-2xl ${getScoreColor(selectedEntry.relevance_score)}`}>
                    {selectedEntry.relevance_score}
                  </div>
                  {selectedEntry.primary_category && (
                    <Badge className="text-sm px-3 py-1 bg-white/5 text-[#F3F4F6] border border-white/10">
                      {selectedEntry.primary_category === 'Efficiency' ? <Zap className="w-4 h-4 mr-1 text-yellow-400" /> : <Brain className="w-4 h-4 mr-1 text-purple-400" />}
                      {selectedEntry.primary_category}
                    </Badge>
                  )}
                </div>
              )}
              {selectedEntry.tags && selectedEntry.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedEntry.tags.map((tag, i) => (
                    <Badge key={i} variant="outline" className="text-xs text-[#6B7280] border-white/10">
                      <Hash className="w-3 h-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
              {selectedEntry.reasoning && (
                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Brain className="w-4 h-4 text-[#00F0FF]" />
                    <span className="font-mono text-xs text-[#00F0FF]">AI ANALYSIS</span>
                  </div>
                  <p className="font-sans text-sm text-[#F3F4F6] leading-relaxed whitespace-pre-wrap">{selectedEntry.reasoning}</p>
                </div>
              )}
              {selectedEntry.profile_context?.recent_tweets && selectedEntry.profile_context.recent_tweets.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#6B7280]" />
                    <span className="font-mono text-xs text-[#6B7280]">RECENT TWEETS</span>
                  </div>
                  <div className="space-y-2">
                    {selectedEntry.profile_context.recent_tweets.slice(0, 3).map((tweet, i) => (
                      <div key={i} className="p-3 rounded bg-white/[0.02] border border-white/5">
                        <p className="font-sans text-xs text-[#F3F4F6]/80">{tweet}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="pt-4 border-t border-white/10 flex flex-wrap gap-4 text-xs text-[#6B7280]">
                <div className="flex items-center gap-1"><Calendar className="w-3 h-3" />Added {formatDate(selectedEntry.added_at)}</div>
                {selectedEntry.source && <div>Source: {selectedEntry.source}</div>}
                {selectedEntry.evaluation_status && <div>Status: {selectedEntry.evaluation_status}</div>}
              </div>
              <div className="flex items-center justify-end pt-4 border-t border-white/10">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { removeFromWatchlist(selectedEntry.entry_id); setSelectedEntry(null); }}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Remove
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============ SAVED TAB CONTENT ============
const SavedTabContent = ({ savedTweets, getAuthHeaders, fetchData }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [actionLoading, setActionLoading] = useState(null);

  const unsaveTweet = async (queueId) => {
    setActionLoading(queueId);
    const authHeaders = getAuthHeaders();
    try {
      await fetch(`${BACKEND_URL}/api/twitter/queue/${queueId}/unsave`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders
      });
      fetchData();
    } catch (error) {
      console.error('Unsave failed:', error);
    } finally {
      setActionLoading(null);
    }
  };

  if (savedTweets.length === 0) {
    return (
      <Card className="bg-white/[0.02] border-white/10">
        <CardContent className="py-12 text-center">
          <Bookmark className="w-10 h-10 text-[#6B7280]/50 mx-auto mb-4" />
          <p className="font-sans text-base text-[#F3F4F6]/80">No saved tweets</p>
          <p className="font-sans text-sm text-[#6B7280] mt-2">
            Tweets you save for later will appear here
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {savedTweets.map(item => (
        <Card key={item.queue_id} className="bg-white/[0.03] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <a
                  href={item.tweet?.tweet_url || `https://x.com/${item.tweet?.author_handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-[#00F0FF] hover:underline flex items-center gap-1 mb-2"
                >
                  @{item.tweet?.author_handle}
                  <ExternalLink className="w-3 h-3" />
                </a>
                <p className="font-sans text-sm text-[#F3F4F6]">{item.tweet?.tweet_text}</p>
                {item.drafts && item.drafts.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <p className="font-mono text-[10px] text-[#6B7280] mb-2">{item.drafts.length} DRAFT{item.drafts.length > 1 ? 'S' : ''} AVAILABLE</p>
                  </div>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => unsaveTweet(item.queue_id)}
                disabled={actionLoading === item.queue_id}
                className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
              >
                {actionLoading === item.queue_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Restore'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};


// ============ RESPONSES TAB CONTENT ============
const ResponsesTabContent = ({ responses, getAuthHeaders, fetchData }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [stats, setStats] = useState(null);
  const [checkingEngagement, setCheckingEngagement] = useState(false);
  const [refreshing, setRefreshing] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      const authHeaders = getAuthHeaders();
      try {
        const res = await fetch(`${BACKEND_URL}/api/twitter/responses/stats`, { 
          credentials: 'include',
          headers: authHeaders 
        });
        if (res.ok) setStats(await res.json());
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    fetchStats();
  }, [getAuthHeaders]);

  const refreshEngagement = async () => {
    setCheckingEngagement(true);
    const authHeaders = getAuthHeaders();
    try {
      await fetch(`${BACKEND_URL}/api/twitter/responses/check-engagement`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders
      });
      setTimeout(fetchData, 3000);
    } catch (error) {
      console.error('Failed to check engagement:', error);
    } finally {
      setTimeout(() => setCheckingEngagement(false), 3000);
    }
  };

  const refreshSingleResponse = async (responseId) => {
    setRefreshing(responseId);
    const authHeaders = getAuthHeaders();
    try {
      await fetch(`${BACKEND_URL}/api/twitter/responses/${responseId}/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders
      });
      fetchData();
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setRefreshing(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-white/[0.03] border-white/10">
            <CardContent className="p-4 text-center">
              <p className="font-mono text-2xl text-[#F3F4F6]">{stats.total_responses}</p>
              <p className="font-sans text-xs text-[#6B7280]">Responses</p>
            </CardContent>
          </Card>
          <Card className="bg-white/[0.03] border-white/10">
            <CardContent className="p-4 text-center">
              <p className="font-mono text-2xl text-pink-400">{stats.total_likes}</p>
              <p className="font-sans text-xs text-[#6B7280]">Total Likes</p>
            </CardContent>
          </Card>
          <Card className="bg-white/[0.03] border-white/10">
            <CardContent className="p-4 text-center">
              <p className="font-mono text-2xl text-green-400">{stats.total_retweets}</p>
              <p className="font-sans text-xs text-[#6B7280]">Retweets</p>
            </CardContent>
          </Card>
          <Card className="bg-white/[0.03] border-white/10">
            <CardContent className="p-4 text-center">
              <p className="font-mono text-2xl text-[#00F0FF]">{stats.total_replies}</p>
              <p className="font-sans text-xs text-[#6B7280]">Replies</p>
            </CardContent>
          </Card>
          <Card className="bg-white/[0.03] border-white/10">
            <CardContent className="p-4 text-center">
              <p className="font-mono text-2xl text-orange-400">{stats.unique_engager_count}</p>
              <p className="font-sans text-xs text-[#6B7280]">Engagers</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Engagers */}
      {stats?.top_engagers?.length > 0 && (
        <Card className="bg-white/[0.03] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 font-mono text-sm text-[#F3F4F6]">
              <TrendingUp className="w-4 h-4 text-[#00F0FF]" />
              TOP ENGAGERS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.top_engagers.map((engager, idx) => (
                <a key={idx} href={`https://x.com/${engager.handle}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.02] border border-white/10 hover:border-[#00F0FF]/30 transition-colors">
                  <span className="font-mono text-xs text-[#00F0FF]">@{engager.handle}</span>
                  {engager.follower_count && <span className="font-mono text-[10px] text-[#6B7280]">{engager.follower_count.toLocaleString()}</span>}
                  <Badge variant="outline" className="text-[10px] text-[#6B7280] border-white/10">{engager.action}</Badge>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Response List Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-[#00F0FF]" />
          <h2 className="font-mono text-sm text-[#F3F4F6]">RESPONSE HISTORY</h2>
        </div>
        <Button onClick={refreshEngagement} disabled={checkingEngagement} size="sm" className="bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 hover:bg-[#00F0FF]/20">
          {checkingEngagement ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Check All Engagement
        </Button>
      </div>

      {/* Response List */}
      {responses.length === 0 ? (
        <Card className="bg-white/[0.02] border-white/10">
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-10 h-10 text-[#6B7280]/50 mx-auto mb-3" />
            <p className="font-sans text-sm text-[#6B7280]">No responses yet</p>
            <p className="font-sans text-xs text-[#6B7280]/60 mt-1">Responses you post or mark as posted will appear here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {responses.map(response => (
            <Card key={response.response_id} className="bg-white/[0.03] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-sans text-xs text-[#6B7280]">Response to</span>
                      <a href={`https://x.com/${response.original_author}`} target="_blank" rel="noopener noreferrer" className="font-mono text-sm text-[#00F0FF] hover:underline">@{response.original_author}</a>
                      <Badge variant="outline" className="text-[10px] text-[#6B7280] border-white/10">{response.response_type}</Badge>
                    </div>
                    <p className="font-sans text-sm text-[#F3F4F6] mb-3">{response.response_text}</p>
                    <div className="flex items-center gap-4 text-xs">
                      <div className="flex items-center gap-1 text-pink-400"><Heart className="w-3 h-3" /><span>{response.likes || 0}</span></div>
                      <div className="flex items-center gap-1 text-green-400"><Repeat className="w-3 h-3" /><span>{response.retweets || 0}</span></div>
                      <div className="flex items-center gap-1 text-[#00F0FF]"><MessageSquare className="w-3 h-3" /><span>{response.replies || 0}</span></div>
                      {response.views && <div className="flex items-center gap-1 text-[#6B7280]"><Eye className="w-3 h-3" /><span>{response.views.toLocaleString()}</span></div>}
                    </div>
                    {response.engagers?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="font-mono text-[10px] text-[#6B7280] mb-2">NOTABLE ENGAGERS</p>
                        <div className="flex flex-wrap gap-1">
                          {response.engagers.slice(0, 5).map((e, idx) => (
                            <a key={idx} href={`https://x.com/${e.handle}`} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-[#00F0FF]/80 hover:text-[#00F0FF]">@{e.handle}</a>
                          ))}
                          {response.engagers.length > 5 && <span className="font-mono text-[10px] text-[#6B7280]">+{response.engagers.length - 5} more</span>}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-3 text-[10px] text-[#6B7280]">
                      <div className="flex items-center gap-1"><Clock className="w-3 h-3" /><span>{formatDate(response.posted_at)}</span></div>
                      <span>via {response.posted_via}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    {response.our_tweet_url && (
                      <a href={response.our_tweet_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded bg-white/[0.02] border border-white/10 hover:border-[#00F0FF]/30 text-[#6B7280] hover:text-[#00F0FF]">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => refreshSingleResponse(response.response_id)} disabled={refreshing === response.response_id || !response.our_tweet_url} className="p-2 text-[#6B7280] hover:text-[#F3F4F6]">
                      {refreshing === response.response_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


// ============ SETTINGS TAB CONTENT ============
const SettingsTabContent = ({ settings: initialSettings, getAuthHeaders, fetchData }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Form state
  const [thesisPrompt, setThesisPrompt] = useState(initialSettings?.thesis?.system_prompt || '');
  const [responsePrompt, setResponsePrompt] = useState(initialSettings?.response_prompt || '');
  const [autoAddThreshold, setAutoAddThreshold] = useState(initialSettings?.thesis?.score_threshold_auto_add || 75);
  const [rejectThreshold, setRejectThreshold] = useState(initialSettings?.thesis?.score_threshold_reject || 40);
  const [seedAccounts, setSeedAccounts] = useState(initialSettings?.discovery?.seed_accounts || []);
  const [newSeed, setNewSeed] = useState('');
  const [searchQueries, setSearchQueries] = useState(initialSettings?.discovery?.grok_search_queries || []);
  const [newQuery, setNewQuery] = useState('');
  const [maxCandidates, setMaxCandidates] = useState(initialSettings?.discovery?.max_candidates_per_run || 20);
  const [userXHandle, setUserXHandle] = useState(initialSettings?.discovery?.user_x_handle || '');
  const [evalHandle, setEvalHandle] = useState('');
  const [evaluating, setEvaluating] = useState(false);
  
  // AI Model settings
  const [composeModel, setComposeModel] = useState(initialSettings?.compose_model || 'gemini-2.5-flash');
  const [composeTemperature, setComposeTemperature] = useState(initialSettings?.compose_temperature || 0.85);
  const [responseModel, setResponseModel] = useState(initialSettings?.response_model || 'gemini-2.5-flash');
  const [responseTemperature, setResponseTemperature] = useState(initialSettings?.response_temperature || 0.85);
  const [postHistoryDepth, setPostHistoryDepth] = useState(initialSettings?.post_history_depth || 20);
  
  // Grok Inspiration settings for Compose tab
  const [thesisSearchQuery, setThesisSearchQuery] = useState(initialSettings?.thesis_search_query || 'AI agents, deep tech infrastructure, human-AI collaboration, founder insights, conviction investing');
  const [inspirationTopics, setInspirationTopics] = useState(initialSettings?.grok_inspiration_topics || ['#AI', '#technology', '#innovation']);
  const [newInspirationTopic, setNewInspirationTopic] = useState('');
  
  // Tweet Scanning Settings (Tweets tab - watchlist scanning)
  const [scanHandlesPerCycle, setScanHandlesPerCycle] = useState(initialSettings?.scan_handles_per_cycle || 15);
  const [scanRotationMode, setScanRotationMode] = useState(initialSettings?.scan_rotation_mode || 'sequential');
  const [scanMinEngagement, setScanMinEngagement] = useState(initialSettings?.scan_min_engagement || 10);
  const [scanMaxAgeHours, setScanMaxAgeHours] = useState(initialSettings?.scan_max_age_hours || 48);
  const [scanPovAlignment, setScanPovAlignment] = useState(initialSettings?.scan_pov_alignment || "I'm interested in AI infrastructure, developer tools, founder insights, conviction investing, and the human side of technology adoption. I value contrarian takes, first-principles thinking, and practical builder perspectives over hype.");
  
  // Cost control state
  const [dailyApiLimit, setDailyApiLimit] = useState(initialSettings?.cost?.daily_api_limit || 100);
  const [maxSeeds, setMaxSeeds] = useState(initialSettings?.cost?.max_seeds_per_cycle || 5);
  const [maxPerSeed, setMaxPerSeed] = useState(initialSettings?.cost?.max_candidates_per_seed || 8);
  const [maxSearchQueries, setMaxSearchQueries] = useState(initialSettings?.cost?.max_search_queries || 2);
  const [enableTrending, setEnableTrending] = useState(initialSettings?.cost?.enable_trending_discovery ?? true);
  const [enableCompetitor, setEnableCompetitor] = useState(initialSettings?.cost?.enable_competitor_analysis ?? true);
  const [costPerCall, setCostPerCall] = useState(initialSettings?.cost?.cost_per_api_call || 0.03);
  
  // Live API usage state
  const [apiUsage, setApiUsage] = useState(null);
  
  // Auto-pause state
  const [autoPauseEnabled, setAutoPauseEnabled] = useState(true);
  const [autoPauseTimeout, setAutoPauseTimeout] = useState(60);
  const [savingAutoPause, setSavingAutoPause] = useState(false);
  
  // Agent Scan Settings state
  const [agentScanNewAccounts, setAgentScanNewAccounts] = useState(initialSettings?.agent_scan_new_accounts ?? true);
  const [agentScanNewTweets, setAgentScanNewTweets] = useState(initialSettings?.agent_scan_new_tweets ?? true);
  const [agentIntervalMinutes, setAgentIntervalMinutes] = useState(initialSettings?.agent_interval_minutes || 15);
  const [savingAgentSettings, setSavingAgentSettings] = useState(false);

  // Fetch auto-pause settings on mount
  useEffect(() => {
    const fetchAutoPause = async () => {
      const authHeaders = getAuthHeaders();
      try {
        const res = await fetch(`${BACKEND_URL}/api/activity/auto-pause`, {
          headers: authHeaders,
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setAutoPauseEnabled(data.auto_pause_enabled ?? true);
          setAutoPauseTimeout(data.auto_pause_timeout_minutes || 60);
        }
      } catch (error) {
        console.error('Failed to fetch auto-pause settings:', error);
      }
    };
    fetchAutoPause();
  }, [BACKEND_URL, getAuthHeaders]);

  // Fetch API usage stats
  const fetchApiUsage = async () => {
    const authHeaders = getAuthHeaders();
    try {
      const res = await fetch(`${BACKEND_URL}/api/sentinel/v2/api-usage`, {
        headers: authHeaders,
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setApiUsage(data);
      }
    } catch (error) {
      console.error('Failed to fetch API usage:', error);
    }
  };

  // Fetch API usage on mount and every 30 seconds
  useEffect(() => {
    fetchApiUsage();
    const interval = setInterval(fetchApiUsage, 30000);
    return () => clearInterval(interval);
  }, [BACKEND_URL, getAuthHeaders]);

  useEffect(() => {
    if (initialSettings) {
      setThesisPrompt(initialSettings.thesis?.system_prompt || '');
      setAutoAddThreshold(initialSettings.thesis?.score_threshold_auto_add || 75);
      setRejectThreshold(initialSettings.thesis?.score_threshold_reject || 40);
      setSeedAccounts(initialSettings.discovery?.seed_accounts || []);
      setSearchQueries(initialSettings.discovery?.grok_search_queries || []);
      setMaxCandidates(initialSettings.discovery?.max_candidates_per_run || 20);
      setUserXHandle(initialSettings.discovery?.user_x_handle || '');
      // Cost settings
      setDailyApiLimit(initialSettings.cost?.daily_api_limit || 100);
      setMaxSeeds(initialSettings.cost?.max_seeds_per_cycle || 5);
      setMaxPerSeed(initialSettings.cost?.max_candidates_per_seed || 8);
      setMaxSearchQueries(initialSettings.cost?.max_search_queries || 2);
      setEnableTrending(initialSettings.cost?.enable_trending_discovery ?? true);
      setEnableCompetitor(initialSettings.cost?.enable_competitor_analysis ?? true);
      setCostPerCall(initialSettings.cost?.cost_per_api_call || 0.03);
      // Grok Inspiration settings
      setThesisSearchQuery(initialSettings.thesis_search_query || 'AI agents, deep tech infrastructure, human-AI collaboration, founder insights, conviction investing');
      setInspirationTopics(initialSettings.grok_inspiration_topics || ['#AI', '#technology', '#innovation']);
      // Tweet Scanning settings
      setScanHandlesPerCycle(initialSettings.scan_handles_per_cycle || 15);
      setScanRotationMode(initialSettings.scan_rotation_mode || 'sequential');
      setScanMinEngagement(initialSettings.scan_min_engagement || 10);
      setScanMaxAgeHours(initialSettings.scan_max_age_hours || 48);
      setScanPovAlignment(initialSettings.scan_pov_alignment || "I'm interested in AI infrastructure, developer tools, founder insights, conviction investing, and the human side of technology adoption. I value contrarian takes, first-principles thinking, and practical builder perspectives over hype.");
      // Agent Scan Settings
      setAgentScanNewAccounts(initialSettings.agent_scan_new_accounts ?? true);
      setAgentScanNewTweets(initialSettings.agent_scan_new_tweets ?? true);
      setAgentIntervalMinutes(initialSettings.agent_interval_minutes || 15);
    }
  }, [initialSettings]);

  // Cost estimator calculation
  const estimateCosts = () => {
    const cyclesPerDay = Math.floor(24 * 60 / agentIntervalMinutes);
    const networkCrawlCalls = Math.min(maxSeeds, seedAccounts.length || 1); // 1 call per seed
    const trendingCalls = enableTrending ? 1 : 0;
    const competitorCalls = enableCompetitor ? 1 : 0;
    const searchCalls = Math.min(maxSearchQueries, searchQueries.length || 1);
    // Profile fetches are FREE if Twitter is connected (uses Twitter API)
    // Only Gemini evaluation calls cost money now
    const evaluationCalls = Math.ceil(maxCandidates / 3); // ~3 evaluations per Gemini call
    
    // Only Grok calls cost money - profile fetches are free via Twitter API!
    const grokCallsPerCycle = networkCrawlCalls + trendingCalls + competitorCalls + searchCalls;
    const dailyGrokCalls = Math.min(cyclesPerDay * grokCallsPerCycle, dailyApiLimit);
    const dailyCost = dailyGrokCalls * costPerCall;
    const monthlyCost = dailyCost * 30;
    
    return { 
      cyclesPerDay, 
      callsPerCycle: grokCallsPerCycle, 
      dailyCalls: dailyGrokCalls, 
      dailyCost, 
      monthlyCost,
      profileFetchesFree: true  // Twitter API is free!
    };
  };
  
  const costs = estimateCosts();

  const saveSettings = async () => {
    setSaving(true);
    const authHeaders = getAuthHeaders();
    try {
      // Save main settings
      const res = await fetch(`${BACKEND_URL}/api/sentinel/v2/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({
          thesis_prompt: thesisPrompt,
          score_threshold_auto_add: autoAddThreshold,
          score_threshold_reject: rejectThreshold,
          seed_accounts: seedAccounts,
          grok_search_queries: searchQueries,
          max_candidates_per_run: maxCandidates,
          user_x_handle: userXHandle,
          // Cost settings
          daily_api_limit: dailyApiLimit,
          max_seeds_per_cycle: maxSeeds,
          max_candidates_per_seed: maxPerSeed,
          max_search_queries: maxSearchQueries,
          enable_trending_discovery: enableTrending,
          enable_competitor_analysis: enableCompetitor,
          cost_per_api_call: costPerCall,
          // Response generation
          response_prompt: responsePrompt,
          // AI Model settings
          compose_model: composeModel,
          compose_temperature: composeTemperature,
          response_model: responseModel,
          response_temperature: responseTemperature,
          post_history_depth: postHistoryDepth,
          // Grok Inspiration settings
          thesis_search_query: thesisSearchQuery,
          grok_inspiration_topics: inspirationTopics,
          // Tweet Scanning settings
          scan_handles_per_cycle: scanHandlesPerCycle,
          scan_rotation_mode: scanRotationMode,
          scan_min_engagement: scanMinEngagement,
          scan_max_age_hours: scanMaxAgeHours,
          scan_pov_alignment: scanPovAlignment,
          // Agent Scan Settings
          agent_scan_new_accounts: agentScanNewAccounts,
          agent_scan_new_tweets: agentScanNewTweets,
          agent_interval_minutes: agentIntervalMinutes
        })
      });
      
      // Save auto-pause settings separately (different endpoint - uses query params)
      await fetch(`${BACKEND_URL}/api/activity/auto-pause?enabled=${autoPauseEnabled}&timeout_minutes=${autoPauseTimeout}`, {
        method: 'PUT',
        headers: authHeaders,
        credentials: 'include'
      });
      
      if (res.ok) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        fetchData();
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const addSeed = () => {
    const handle = newSeed.toLowerCase().replace('@', '').trim();
    if (handle && !seedAccounts.includes(handle)) {
      setSeedAccounts([...seedAccounts, handle]);
      setNewSeed('');
    }
  };

  const removeSeed = (handle) => setSeedAccounts(seedAccounts.filter(s => s !== handle));

  const addQuery = () => {
    if (newQuery.trim() && !searchQueries.includes(newQuery.trim())) {
      setSearchQueries([...searchQueries, newQuery.trim()]);
      setNewQuery('');
    }
  };

  const removeQuery = (query) => setSearchQueries(searchQueries.filter(q => q !== query));

  const evaluateHandle = async () => {
    if (!evalHandle.trim()) return;
    setEvaluating(true);
    const authHeaders = getAuthHeaders();
    try {
      await fetch(`${BACKEND_URL}/api/sentinel/v2/candidates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({ screen_name: evalHandle })
      });
      setEvalHandle('');
    } catch (error) {
      console.error('Evaluation failed:', error);
    } finally {
      setEvaluating(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Settings Sub-tabs */}
      <Tabs defaultValue="social" className="w-full">
        <TabsList className="bg-white/5 border border-white/10 p-1">
          <TabsTrigger 
            value="social" 
            className="data-[state=active]:bg-[#00F0FF]/20 data-[state=active]:text-[#00F0FF] font-mono text-sm"
          >
            SOCIAL
          </TabsTrigger>
          <TabsTrigger 
            value="site" 
            className="data-[state=active]:bg-[#00F0FF]/20 data-[state=active]:text-[#00F0FF] font-mono text-sm"
          >
            SITE
          </TabsTrigger>
        </TabsList>

        {/* Social Settings Tab */}
        <TabsContent value="social" className="mt-6">
          <div className="space-y-6">
            {/* Save Button - Fixed at top */}
            <div className="flex justify-between items-center sticky top-0 bg-[#050505]/95 backdrop-blur py-3 -mt-3 z-10">
              <p className="font-sans text-xs text-[#6B7280]">Changes require saving to take effect</p>
              <Button onClick={saveSettings} disabled={saving} className={saveSuccess ? 'bg-green-500 text-white' : 'bg-[#00F0FF] text-[#050505]'}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : saveSuccess ? <CheckCircle className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {saveSuccess ? 'Saved!' : 'Save All Settings'}
              </Button>
            </div>

            {/* ========== SECTION 1: AGENT CONTROL ========== */}
            <Card className="bg-white/[0.03] border-white/10 border-l-4 border-l-[#00F0FF]">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 font-mono text-[#F3F4F6]">
                  <Activity className="w-5 h-5 text-[#00F0FF]" />
                  AGENT CONTROL
                </CardTitle>
                <p className="font-sans text-xs text-[#6B7280] mt-1">Background agent requires Twitter OAuth for monitoring</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Scan Types */}
                <div className="grid md:grid-cols-2 gap-4">
                  <label className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/10 cursor-pointer hover:border-white/20">
                    <input type="checkbox" checked={agentScanNewAccounts} onChange={(e) => setAgentScanNewAccounts(e.target.checked)} className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#00F0FF]" />
                    <div>
                      <span className="font-mono text-sm text-[#F3F4F6]">Discovery (Accounts)</span>
                      <p className="font-sans text-xs text-amber-400">Uses Grok API ($)</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/10 cursor-pointer hover:border-white/20">
                    <input type="checkbox" checked={agentScanNewTweets} onChange={(e) => setAgentScanNewTweets(e.target.checked)} className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#00F0FF]" />
                    <div>
                      <span className="font-mono text-sm text-[#F3F4F6]">Monitoring (Tweets)</span>
                      <p className="font-sans text-xs text-green-400">Uses Twitter API (FREE)</p>
                    </div>
                  </label>
                </div>
                
                {/* Info notices */}
                {agentScanNewAccounts && (
                  <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20">
                    <p className="font-sans text-xs text-amber-400 flex items-center gap-2">
                      <AlertCircle className="w-3 h-3" /> Discovery uses Grok API which has costs. Disable to save money.
                    </p>
                  </div>
                )}
                <div className="p-2 rounded bg-blue-500/10 border border-blue-500/20">
                  <p className="font-sans text-xs text-blue-400 flex items-center gap-2">
                    <Twitter className="w-3 h-3" /> Monitoring requires Twitter OAuth. Connect in Settings → Site.
                  </p>
                </div>
                
                {/* Interval & Auto-pause in one row */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-white/[0.02] border border-white/10">
                    <label className="block font-mono text-xs text-[#6B7280] mb-2">RUN EVERY: {agentIntervalMinutes} minutes</label>
                    <Slider value={[agentIntervalMinutes]} onValueChange={([v]) => setAgentIntervalMinutes(v)} min={5} max={60} step={5} className="w-full" />
                  </div>
                  <div className="p-3 rounded-lg bg-white/[0.02] border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <label className="font-mono text-xs text-[#6B7280]">AUTO-PAUSE WHEN INACTIVE</label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={autoPauseEnabled} onChange={(e) => setAutoPauseEnabled(e.target.checked)} className="w-4 h-4 rounded" />
                        <span className="font-mono text-xs text-[#F3F4F6]">{autoPauseEnabled ? 'ON' : 'OFF'}</span>
                      </label>
                    </div>
                    {autoPauseEnabled && (
                      <div>
                        <Slider value={[autoPauseTimeout]} onValueChange={([v]) => setAutoPauseTimeout(v)} min={15} max={240} step={15} className="w-full" />
                        <p className="font-sans text-xs text-[#6B7280] mt-1">Pause after {autoPauseTimeout}m of dashboard inactivity</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {!agentScanNewAccounts && !agentScanNewTweets && (
                  <div className="p-2 rounded bg-red-500/10 border border-red-500/20">
                    <p className="font-sans text-xs text-red-400 flex items-center gap-2">
                      <AlertCircle className="w-3 h-3" /> No scan types selected - agent will not do anything
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ========== SECTION 2: COMPOSE TAB ========== */}
            <div className="space-y-4">
              <h3 className="font-mono text-sm text-[#6B7280] flex items-center gap-2 px-1">
                <Sparkles className="w-4 h-4" /> COMPOSE TAB SETTINGS
              </h3>
              
              {/* Full width cards */}
              <Card className="bg-white/[0.03] border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="font-mono text-sm text-[#F3F4F6] flex items-center gap-2">
                    <Brain className="w-4 h-4 text-purple-400" /> AI Model & Inspiration
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Model settings in a row */}
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block font-mono text-xs text-[#6B7280] mb-1">MODEL</label>
                      <select value={composeModel} onChange={(e) => setComposeModel(e.target.value)} className="w-full bg-white/5 border border-white/10 text-[#F3F4F6] rounded-md px-3 py-2 text-sm font-mono">
                        <optgroup label="Gemini"><option value="gemini-2.5-flash">Gemini 2.5 Flash</option><option value="gemini-2.5-pro">Gemini 2.5 Pro</option></optgroup>
                        <optgroup label="OpenAI"><option value="gpt-4o">GPT-4o</option><option value="gpt-4o-mini">GPT-4o Mini</option></optgroup>
                        <optgroup label="Claude"><option value="claude-4-sonnet-20250514">Claude 4 Sonnet</option></optgroup>
                      </select>
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-[#6B7280] mb-1">TEMPERATURE: {composeTemperature.toFixed(2)}</label>
                      <Slider value={[composeTemperature]} onValueChange={([v]) => setComposeTemperature(v)} min={0} max={1} step={0.05} className="w-full mt-3" />
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-[#6B7280] mb-1">POST HISTORY: {postHistoryDepth} posts</label>
                      <Slider value={[postHistoryDepth]} onValueChange={([v]) => setPostHistoryDepth(v)} min={0} max={100} step={5} className="w-full mt-3" />
                    </div>
                  </div>
                  
                  {/* Thesis search - full width */}
                  <div>
                    <label className="block font-mono text-xs text-[#6B7280] mb-1">THESIS SEARCH QUERY</label>
                    <Textarea value={thesisSearchQuery} onChange={(e) => setThesisSearchQuery(e.target.value)} className="bg-white/5 border-white/10 text-[#F3F4F6] text-sm min-h-[80px]" placeholder="AI agents, deep tech..." />
                    <p className="font-sans text-xs text-[#6B7280] mt-1">Used when clicking Thesis match in Compose</p>
                  </div>
                  
                  {/* Topic buttons */}
                  <div>
                    <label className="block font-mono text-xs text-[#6B7280] mb-1">INSPIRATION TOPIC BUTTONS</label>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {inspirationTopics.map((topic, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs text-amber-400 border-amber-400/30 pr-1">
                          {topic}
                          <button onClick={() => setInspirationTopics(inspirationTopics.filter((_, i) => i !== idx))} className="ml-1 hover:text-red-400"><X className="w-3 h-3" /></button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input value={newInspirationTopic} onChange={(e) => setNewInspirationTopic(e.target.value)} placeholder="#topic" className="bg-white/5 border-white/10 text-[#F3F4F6] text-sm" onKeyDown={(e) => { if (e.key === 'Enter' && newInspirationTopic.trim()) { setInspirationTopics([...inspirationTopics, newInspirationTopic.trim()]); setNewInspirationTopic(''); }}} />
                      <Button size="sm" onClick={() => { if (newInspirationTopic.trim()) { setInspirationTopics([...inspirationTopics, newInspirationTopic.trim()]); setNewInspirationTopic(''); }}} className="bg-white/10"><Plus className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ========== SECTION 3: TWEETS TAB ========== */}
            <div className="space-y-4">
              <h3 className="font-mono text-sm text-[#6B7280] flex items-center gap-2 px-1">
                <Twitter className="w-4 h-4" /> TWEETS TAB SETTINGS
              </h3>
              
              {/* Full width card */}
              <Card className="bg-white/[0.03] border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="font-mono text-sm text-[#F3F4F6] flex items-center gap-2">
                    <Activity className="w-4 h-4 text-green-400" /> Scanning & AI Responses
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* POV Alignment - Full width, taller */}
                  <div>
                    <label className="block font-mono text-xs text-[#6B7280] mb-1">POV ALIGNMENT (What topics interest you for engagement?)</label>
                    <Textarea value={scanPovAlignment} onChange={(e) => setScanPovAlignment(e.target.value)} className="bg-white/5 border-white/10 text-[#F3F4F6] min-h-[120px] text-sm" placeholder="I'm interested in AI infrastructure, developer tools, founder insights..." />
                  </div>
                  
                  {/* Scanning controls in a row */}
                  <div className="grid md:grid-cols-4 gap-4">
                    <div>
                      <label className="block font-mono text-xs text-[#6B7280] mb-1">HANDLES/SCAN: {scanHandlesPerCycle}</label>
                      <Slider value={[scanHandlesPerCycle]} onValueChange={([v]) => setScanHandlesPerCycle(v)} min={5} max={50} step={5} className="w-full" />
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-[#6B7280] mb-1">MAX AGE: {scanMaxAgeHours}h</label>
                      <Slider value={[scanMaxAgeHours]} onValueChange={([v]) => setScanMaxAgeHours(v)} min={6} max={168} step={6} className="w-full" />
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-[#6B7280] mb-1">MIN ENGAGEMENT: {scanMinEngagement}</label>
                      <Slider value={[scanMinEngagement]} onValueChange={([v]) => setScanMinEngagement(v)} min={0} max={100} step={5} className="w-full" />
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-[#6B7280] mb-1">ROTATION</label>
                      <select value={scanRotationMode} onChange={(e) => setScanRotationMode(e.target.value)} className="w-full bg-white/5 border border-white/10 text-[#F3F4F6] rounded-md p-2 text-sm">
                        <option value="sequential">Sequential</option>
                        <option value="random">Random</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* AI Response settings */}
                  <div className="pt-3 border-t border-white/10">
                    <label className="block font-mono text-xs text-[#6B7280] mb-3">AI RESPONSE GENERATION</label>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-mono text-xs text-[#6B7280] mb-1">MODEL</label>
                        <select value={responseModel} onChange={(e) => setResponseModel(e.target.value)} className="w-full bg-white/5 border border-white/10 text-[#F3F4F6] rounded-md px-2 py-2 text-sm font-mono">
                          <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
                          <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
                          <option value="gpt-4o">GPT-4o</option>
                          <option value="gpt-4o-mini">GPT-4o Mini</option>
                        </select>
                      </div>
                      <div>
                        <label className="block font-mono text-xs text-[#6B7280] mb-1">TEMPERATURE: {responseTemperature.toFixed(2)}</label>
                        <Slider value={[responseTemperature]} onValueChange={([v]) => setResponseTemperature(v)} min={0} max={1} step={0.05} className="w-full mt-2" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Response Prompt - Full width, taller */}
                  <div>
                    <label className="block font-mono text-xs text-[#6B7280] mb-1">RESPONSE PROMPT (Instructions for AI when generating draft replies)</label>
                    <Textarea value={responsePrompt} onChange={(e) => setResponsePrompt(e.target.value)} className="bg-white/5 border-white/10 text-[#F3F4F6] min-h-[150px] text-sm font-mono" placeholder="Custom prompt for generating responses..." />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ========== SECTION 4: DISCOVERY (ACCOUNTS TAB) ========== */}
            <div className="space-y-4">
              <h3 className="font-mono text-sm text-[#6B7280] flex items-center gap-2 px-1">
                <Users className="w-4 h-4" /> DISCOVERY SETTINGS (Accounts Tab)
              </h3>
              
              {/* Full width card */}
              <Card className="bg-white/[0.03] border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="font-mono text-sm text-[#F3F4F6] flex items-center gap-2">
                    <Search className="w-4 h-4 text-[#00F0FF]" /> Sources & Evaluation
                    <span className="text-xs text-amber-400 ml-2">(Uses Grok API)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Sources in a grid */}
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block font-mono text-xs text-[#6B7280] mb-1">YOUR X HANDLE</label>
                      <Input value={userXHandle} onChange={(e) => setUserXHandle(e.target.value.replace('@', '').trim())} placeholder="wycocapital" className="bg-white/5 border-white/10 text-[#F3F4F6] text-sm" />
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-[#6B7280] mb-1">SEED ACCOUNTS</label>
                      <div className="flex flex-wrap gap-1 mb-2 max-h-16 overflow-y-auto">
                        {seedAccounts.map(handle => (
                          <Badge key={handle} variant="outline" className="text-xs text-[#00F0FF] border-[#00F0FF]/30 pr-1">
                            @{handle}
                            <button onClick={() => removeSeed(handle)} className="ml-1 hover:text-red-400"><X className="w-3 h-3" /></button>
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input value={newSeed} onChange={(e) => setNewSeed(e.target.value)} placeholder="@username" className="bg-white/5 border-white/10 text-[#F3F4F6] text-sm" onKeyDown={(e) => e.key === 'Enter' && addSeed()} />
                        <Button size="sm" onClick={addSeed} disabled={!newSeed.trim()} className="bg-white/10"><Plus className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-[#6B7280] mb-1">GROK SEARCH QUERIES</label>
                      <div className="space-y-1 mb-2 max-h-16 overflow-y-auto">
                        {searchQueries.map((query, idx) => (
                          <div key={idx} className="flex items-center justify-between p-1 rounded bg-white/[0.02] text-xs">
                            <span className="text-[#F3F4F6] truncate">{query}</span>
                            <button onClick={() => removeQuery(query)} className="text-[#6B7280] hover:text-red-400 ml-2"><X className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input value={newQuery} onChange={(e) => setNewQuery(e.target.value)} placeholder="AI agents builders..." className="bg-white/5 border-white/10 text-[#F3F4F6] text-sm" onKeyDown={(e) => e.key === 'Enter' && addQuery()} />
                        <Button size="sm" onClick={addQuery} disabled={!newQuery.trim()} className="bg-white/10"><Plus className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* AI Evaluation Prompt - Full width, taller */}
                  <div className="pt-3 border-t border-white/10">
                    <label className="block font-mono text-xs text-[#6B7280] mb-1">AI EVALUATION PROMPT (How to score new account profiles)</label>
                    <Textarea value={thesisPrompt} onChange={(e) => setThesisPrompt(e.target.value)} className="bg-white/5 border-white/10 text-[#F3F4F6] min-h-[150px] text-sm font-mono" placeholder="Thesis prompt for evaluating profiles..." />
                  </div>
                  
                  {/* Thresholds in a row */}
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block font-mono text-xs text-[#6B7280] mb-1">AUTO-ADD THRESHOLD: ≥{autoAddThreshold}</label>
                      <Slider value={[autoAddThreshold]} onValueChange={([v]) => setAutoAddThreshold(v)} min={50} max={100} step={5} className="w-full" />
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-[#6B7280] mb-1">REJECT THRESHOLD: &lt;{rejectThreshold}</label>
                      <Slider value={[rejectThreshold]} onValueChange={([v]) => setRejectThreshold(v)} min={0} max={50} step={5} className="w-full" />
                    </div>
                    <div>
                      <label className="block font-mono text-xs text-[#6B7280] mb-1">MAX CANDIDATES: {maxCandidates}/cycle</label>
                      <Slider value={[maxCandidates]} onValueChange={([v]) => setMaxCandidates(v)} min={5} max={50} step={5} className="w-full" />
                    </div>
                  </div>
                  <p className="font-sans text-xs text-[#6B7280]">Profiles scoring {rejectThreshold}-{autoAddThreshold - 1} go to manual review</p>
                </CardContent>
              </Card>
            </div>

            {/* ========== SECTION 5: UTILITIES ========== */}
            <div className="space-y-4">
              <h3 className="font-mono text-sm text-[#6B7280] flex items-center gap-2 px-1">
                <Settings className="w-4 h-4" /> UTILITIES
              </h3>
              
              <Card className="bg-white/[0.03] border-white/10">
                <CardContent className="py-4 space-y-4">
                  {/* Manual Evaluation */}
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <label className="block font-mono text-xs text-[#6B7280] mb-1">MANUAL EVALUATION</label>
                      <div className="flex gap-2">
                        <Input value={evalHandle} onChange={(e) => setEvalHandle(e.target.value)} placeholder="@username" className="bg-white/5 border-white/10 text-[#F3F4F6]" onKeyDown={(e) => e.key === 'Enter' && !evaluating && evaluateHandle()} disabled={evaluating} />
                        <Button onClick={evaluateHandle} disabled={!evalHandle.trim() || evaluating} className="bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30">
                          {evaluating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" />Evaluate</>}
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Cost Estimator - Read only */}
                  <div className="p-3 rounded-lg bg-white/[0.02] border border-white/10">
                    <label className="block font-mono text-xs text-[#6B7280] mb-2">ESTIMATED COSTS</label>
                    <div className="grid grid-cols-5 gap-2 text-center">
                      <div><p className="font-mono text-lg text-green-400">{costs.cyclesPerDay}</p><p className="text-[10px] text-[#6B7280]">cycles/day</p></div>
                      <div><p className="font-mono text-lg text-[#F3F4F6]">{costs.callsPerCycle}</p><p className="text-[10px] text-[#6B7280]">calls/cycle</p></div>
                      <div><p className="font-mono text-lg text-[#F3F4F6]">{costs.dailyCalls}</p><p className="text-[10px] text-[#6B7280]">calls/day</p></div>
                      <div><p className="font-mono text-lg text-yellow-400">${costs.dailyCost.toFixed(2)}</p><p className="text-[10px] text-[#6B7280]">daily</p></div>
                      <div><p className="font-mono text-lg text-yellow-400">${costs.monthlyCost.toFixed(2)}</p><p className="text-[10px] text-[#6B7280]">monthly</p></div>
                    </div>
                  </div>
                  
                  {/* Live API Usage - Real-time tracking */}
                  {apiUsage && (
                    <div className="p-3 rounded-lg bg-white/[0.02] border border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <label className="font-mono text-xs text-[#6B7280]">LIVE API USAGE (TODAY)</label>
                        <span className="text-[10px] text-[#6B7280]">{apiUsage.today?.date}</span>
                      </div>
                      <div className="grid grid-cols-4 gap-3 text-center mb-3">
                        <div className="p-2 rounded bg-red-500/10 border border-red-500/20">
                          <p className="font-mono text-xl text-red-400">{apiUsage.today?.grok_calls || 0}</p>
                          <p className="text-[10px] text-red-400/70">Grok ($)</p>
                        </div>
                        <div className="p-2 rounded bg-blue-500/10 border border-blue-500/20">
                          <p className="font-mono text-xl text-blue-400">{apiUsage.today?.twitter_calls || 0}</p>
                          <p className="text-[10px] text-blue-400/70">Twitter (free)</p>
                        </div>
                        <div className="p-2 rounded bg-green-500/10 border border-green-500/20">
                          <p className="font-mono text-xl text-green-400">{apiUsage.today?.gemini_calls || 0}</p>
                          <p className="text-[10px] text-green-400/70">Gemini (free)</p>
                        </div>
                        <div className="p-2 rounded bg-yellow-500/10 border border-yellow-500/20">
                          <p className="font-mono text-xl text-yellow-400">${apiUsage.today?.estimated_cost?.toFixed(2) || '0.00'}</p>
                          <p className="text-[10px] text-yellow-400/70">Cost Today</p>
                        </div>
                      </div>
                      {/* Grok Limit Progress */}
                      <div className="mb-2">
                        <div className="flex justify-between text-[10px] text-[#6B7280] mb-1">
                          <span>Grok Daily Limit</span>
                          <span>{apiUsage.today?.grok_calls || 0} / {apiUsage.today?.daily_limit || dailyApiLimit}</span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${
                              (apiUsage.today?.limit_percentage || 0) > 80 ? 'bg-red-500' : 
                              (apiUsage.today?.limit_percentage || 0) > 50 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(100, apiUsage.today?.limit_percentage || 0)}%` }}
                          />
                        </div>
                      </div>
                      {/* 7-day summary */}
                      <div className="flex items-center justify-between text-[10px] text-[#6B7280] pt-2 border-t border-white/5">
                        <span>7-Day Total:</span>
                        <span>
                          <span className="text-red-400">{apiUsage.seven_day?.grok_calls || 0} Grok</span>
                          {' • '}
                          <span className="text-blue-400">{apiUsage.seven_day?.twitter_calls || 0} Twitter</span>
                          {' • '}
                          <span className="text-green-400">{apiUsage.seven_day?.gemini_calls || 0} Gemini</span>
                          {' • '}
                          <span className="text-yellow-400">${apiUsage.seven_day?.estimated_cost?.toFixed(2) || '0.00'}</span>
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Site Settings Tab */}
        <TabsContent value="site" className="mt-6">
          <SiteSettingsTab getAuthHeaders={getAuthHeaders} />
        </TabsContent>
      </Tabs>
    </div>
  );
};


export default SentinelDashboard;
